import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Camera, CameraOff, Mic, VideoOff, MicOff, Phone, Clock, ArrowLeft, AlertTriangle, CheckCircle, XCircle, Loader, Send, Eye, EyeOff, RefreshCw, Sparkles } from 'lucide-react';
import TTSApi from '../services/ttsApi';

import { InterviewChatAPI } from '../services/interviewChatApi';
import VoiceWaveform from '../components/VoiceWaveform';

import InterviewEvaluationAPI from '../services/interviewEvaluationApi';
import { InterviewRecordAPI } from '../services/interviewRecordApi';
import { InterviewSessionManager } from '../services/interviewSessionManager';
import { AIReferenceAPI } from '../services/aiReferenceApi';
import { InterviewConversationAPI } from '../services/interviewConversationApi';
import { ExpressionAnalysisAPI } from '../services/expressionAnalysisApi';
import { createFunAsrInterviewSpeechRecognition } from '../services/funAsrInterviewSpeechRecognition';
import pointsAPI from '../services/pointsApi';
import { getCurrentUserId, isTrialUser, TRIAL_TIME_LIMIT } from '../utils/subscriptionUtils';
import TrialLimitModal from '../components/common/TrialLimitModal';
import trialUsageAPI from '../services/trialUsageApi';
import customInterviewApi from '../services/customInterviewApi';

// ========= WebAudio 全局播放器 =========
const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 22050 });
let scheduledTime = audioCtx.currentTime;

// 添加全局变量来追踪语音播放状态
let activeAudioSources = [];

// 添加TTS播放完成回调
let onTTSCompleteCallback = null;

// 添加防抖机制，防止短时间内多次触发sendUserMessage
let lastUserMessageTime = 0;
let pendingUserMessage = null;
let userMessageDebounceTimer = null;
const USER_MESSAGE_DEBOUNCE_DELAY = 1000; // 1秒防抖延迟

// 控制当前 TTS 流，以便在下一次播放时可以主动取消，避免多个音频并行播放
let currentTTSAbortController = null;
// 控制聊天流的取消，用于在退出页面时立即中断
let currentChatAbortController = null;

// 全局退出标志，用于防止在退出面试时继续处理音频
let isLeavingInterview = false;

export const appendPcmChunk = (base64Str) => {
    if (isLeavingInterview) {
        return;
    }

    if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {
        });
    }

    try {
        const binary = atob(base64Str);
        const len = binary.length / 2;
        const int16 = new Int16Array(len);
        for (let i = 0; i < len; i++) {
            int16[i] = (binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8)) << 0;
        }
        const audioBuffer = audioCtx.createBuffer(1, int16.length, audioCtx.sampleRate);
        const float32 = audioBuffer.getChannelData(0);
        for (let i = 0; i < int16.length; i++) {
            float32[i] = int16[i] / 32768;
        }
        const bufferSource = audioCtx.createBufferSource();
        bufferSource.buffer = audioBuffer;
        bufferSource.connect(audioCtx.destination);
        const now = audioCtx.currentTime;
        if (scheduledTime < now) scheduledTime = now;
        bufferSource.start(scheduledTime);
        scheduledTime += audioBuffer.duration;
        activeAudioSources.push(bufferSource);
        bufferSource.onended = () => {
            activeAudioSources = activeAudioSources.filter(src => src !== bufferSource);
            if (onTTSCompleteCallback && activeAudioSources.length === 0) {
                onTTSCompleteCallback();
            }
        };
    } catch (error) {
    }
};

const InterviewPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // 面试官性别到头像的映射函数（返回基础路径，不包含扩展名）
    const getInterviewerAvatarBase = (interviewerType) => {
        // 检查是否为自定义面试
        const interviewType = location.state?.interviewType || location.state?.configData?.interviewType || 'job';
        if (interviewType === 'custom') {
            return '/images/interviewers/interviewer-female';
        }

        // 女性面试官：hr_xiaomei（小美）、pm_scholar_xiaoli（小李）
        const femaleInterviewers = ['hr_xiaomei', 'pm_scholar_xiaoli'];

        if (femaleInterviewers.includes(interviewerType)) {
            return '/images/interviewers/interviewer-female';
        } else {
            // 男性面试官：tech_guru_laozhang（老张）、boss_oldwang（老王）、ceo_laoliu（老刘）、peer_sarcastic_xiaochen（小陈）
            return '/images/interviewers/interviewer-male';
        }
    };
    const videoRef = useRef(null);
    const videoAreaRef = useRef(null); // 视频区域用于全屏
    const streamCleanupRef = useRef(null);
    // 防止开发模式下严格模式或状态抖动导致多次开场
    const hasStartedRef = useRef(false);
    const [asrActiveSeconds, setAsrActiveSeconds] = useState(0);
    const [ttsCnEnChars, setTtsCnEnChars] = useState(0);
    const [ttsJpKrChars, setTtsJpKrChars] = useState(0);

    // 统一更新流并刷新清理函数
    const applyNewStream = (newStream) => {
        if (streamCleanupRef.current) {
            streamCleanupRef.current();
        }
        streamCleanupRef.current = () => {
            newStream.getTracks().forEach((t) => t.stop());
        };
        setStream(newStream);
        if (videoRef.current) videoRef.current.srcObject = newStream;
    };
    const [stream, setStream] = useState(null);
    const [interviewConfig, setInterviewConfig] = useState(null);

    // 从路由状态获取面试配置
    useEffect(() => {
        if (location.state?.configData) {
            setInterviewConfig(location.state.configData);
        }
    }, [location]);

    // 检查是否有可恢复的面试会话
    useEffect(() => {
        const checkResumableSession = async () => {
            try {
                // 检查是否是从恢复会话进入的
                if (location.state?.resumeSession && location.state?.sessionData) {
                    const sessionData = location.state.sessionData;

                    // 恢复会话状态
                    setCurrentSessionId(sessionData.recordId);
                    setIsSessionActive(true);
                    setMessages(sessionData.messages || []);
                    setElapsedSeconds(sessionData.elapsedSeconds || 0);
                    setInterviewConfig(sessionData.interviewConfig);
                    setHasHandledResume(false); // 重置恢复处理状态

                    // 为自定义面试恢复对话会话ID
                    const interviewType = sessionData.interviewConfig?.interviewType || 'job';
                    if (interviewType === 'custom') {
                        const customSessionId = `custom_${sessionData.recordId}_resume_${Date.now()}`;
                        setCustomInterviewSessionId(customSessionId);
                    }

                    // 恢复面试题目（如果会话数据中有）
                    if (sessionData.questions) {
                        // 将面试题目设置到location.state中，以便评估时使用
                        if (!location.state) location.state = {};
                        location.state.questions = sessionData.questions;
                    }

                    // 恢复活跃会话
                    await InterviewSessionManager.resumeSession(sessionData.recordId, sessionData);

                    // 直接显示面试界面
                    setShowInterviewUI(true);
                    setStarted(true);
                } else {
                    // 检查是否有其他可恢复的会话
                    const sessions = InterviewSessionManager.getActiveSessions();
                    if (sessions.length > 0) {
                        // 检查是否有暂停的会话
                        const pausedSessions = sessions.filter(s => s.status === 'paused');
                        if (pausedSessions.length > 0) {
                            // 这里可以显示恢复选项，暂时先自动恢复第一个
                            const sessionToResume = pausedSessions[0];

                            // 恢复会话状态
                            setCurrentSessionId(sessionToResume.recordId);
                            setIsSessionActive(true);
                            setMessages(sessionToResume.messages || []);
                            setElapsedSeconds(sessionToResume.elapsedSeconds || 0);
                            setInterviewConfig(sessionToResume.interviewConfig);

                            // 为自定义面试恢复对话会话ID
                            const interviewType = sessionToResume.interviewConfig?.interviewType || 'job';
                            if (interviewType === 'custom') {
                                const customSessionId = `custom_${sessionToResume.recordId}_auto_resume_${Date.now()}`;
                                setCustomInterviewSessionId(customSessionId);
                            }

                            // 恢复面试题目（如果会话数据中有）
                            if (sessionToResume.questions) {
                                // 将面试题目设置到location.state中，以便评估时使用
                                if (!location.state) location.state = {};
                                location.state.questions = sessionToResume.questions;
                            }

                            // 恢复活跃会话
                            await InterviewSessionManager.resumeSession(sessionToResume.recordId, sessionToResume);
                        }
                    }
                }
            } catch (error) {
            }
        };

        checkResumableSession();
    }, [location]);

    // 初始化时立即请求权限和检测设备
    useEffect(() => {
        const initializeDevices = async () => {
            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    audio: true
                });

                applyNewStream(mediaStream);
                setMicEnabled(true);
            } catch (error) {
                setMicEnabled(false);
            }
        };

        initializeDevices();

        return () => {
            if (streamCleanupRef.current) {
                streamCleanupRef.current();
                streamCleanupRef.current = null;
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 实际开始面试的逻辑（倒计时结束后调用）
    const actuallyStartInterview = useCallback(async () => {
        try {

            // 检查体验版用户的累计试用时长
            const userId = getCurrentUserId();
            let trialUser = false;
            if (userId) {
                trialUser = await isTrialUser(userId);
                setIsTrial(trialUser); // 立即更新状态

                if (trialUser) {
                    // 检查累计试用时长
                    const usageCheck = await trialUsageAPI.checkUsage('mock_interview');
                    if (!usageCheck.can_use) {
                        // 累计时长已用完，显示限制弹窗
                        setShowTrialLimitModal(true);
                        setCountdown(null);
                        return;
                    }
                }
            }

            // 重置计时，从0开始
            setElapsedSeconds(0);
            // 记录开始时间戳
            interviewStartTsRef.current = Date.now();
            // 记录试用开始时间（用于单次会话限制）
            if (trialUser) {
                trialStartTimeRef.current = Date.now();
                hasShownWarningRef.current = false; // 重置预警状态
                setShowTrialWarningModal(false); // 重置预警弹框

                // 获取当日剩余试用时长，作为本次会话的初始剩余时长
                try {
                    const userId = getCurrentUserId();
                    if (userId) {
                        const trialStatus = await trialUsageAPI.getStatus('mock_interview');
                        // 设置初始剩余时长（当日累计消耗后的剩余时长）
                        initialTrialRemainingRef.current = trialStatus.remaining || 0;
                        setRemainingTrialSeconds(initialTrialRemainingRef.current);
                    }
                } catch (error) {
                    initialTrialRemainingRef.current = remainingTrialSeconds || TRIAL_TIME_LIMIT;
                }
            }
            // 重置开场标记，确保新会话只触发一次开场
            hasStartedRef.current = false;
            // 尝试在用户点击「开始」时解锁音频，避免浏览器自动播放策略导致无声
            try {
                if (audioCtx.state !== 'running') {
                    await audioCtx.resume();
                    const buf = audioCtx.createBuffer(1, 1, 22050);
                    const src = audioCtx.createBufferSource();
                    src.buffer = buf;
                    src.connect(audioCtx.destination);
                    src.start(0);
                }
            } catch (e) {
            }

            // 创建初始的面试记录（进行中）
            const record = await saveInterviewRecord(null, false, 'in-progress');
            setCurrentSessionId(record.id);
            setIsSessionActive(true);

            // 为自定义面试生成对话会话ID
            const interviewType = location.state?.interviewType || location.state?.configData?.interviewType || 'job';
            if (interviewType === 'custom') {
                const customSessionId = `custom_${record.id}_${Date.now()}`;
                setCustomInterviewSessionId(customSessionId);
            }

            // 保存活跃会话，包含面试题目
            await InterviewSessionManager.saveActiveSession({
                recordId: record.id,
                interviewConfig: interviewConfig,
                questions: location.state?.questions || [], // 添加面试题目
                messages: [],
                elapsedSeconds: 0,
                status: 'active'
            });

            setShowInterviewUI(true);

        } catch (error) {
            alert('开始面试失败，请重试');
            setCountdown(null); // 出错时取消倒计时
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, interviewConfig]);

    // 面试界面显示与倒计时状态
    const [showInterviewUI, setShowInterviewUI] = useState(false);
    // 倒计时状态：3, 2, 1, 'start', null（不显示倒计时）
    const [countdown, setCountdown] = useState(null);
    // 确认卡片超时定时器引用
    const confirmDialogTimeoutRef = useRef(null);

    // ===== 倒计时提示音 =====
    const audioCtxRef = useRef(null);

    const initAudio = () => {
        try {
            if (!audioCtxRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (Ctx) {
                    audioCtxRef.current = new Ctx();
                }
            }
            if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
                audioCtxRef.current.resume().catch(() => { });
            }
        } catch (_) { }
    };

    const playTone = (frequency, durationMs = 150, volume = 0.2) => {
        try {
            const ctx = audioCtxRef.current;
            if (!ctx) return;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(frequency, ctx.currentTime);
            gain.gain.setValueAtTime(0, ctx.currentTime);
            gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + durationMs / 1000 + 0.02);
        } catch (_) { }
    };

    const playTickBeep = useCallback(() => playTone(700, 140, 0.18), []);
    const playStartBeep = useCallback(() => playTone(950, 220, 0.22), []);
    // 更响亮的提示音，用于30秒提醒与5秒截止阶段
    const playLoudTickBeep = useCallback(() => playTone(1000, 160, 0.45), []);
    const playLoudStartBeep = useCallback(() => playTone(1200, 250, 0.6), []);

    // 开始面试（先显示倒计时）
    const handleStartInterview = () => {
        isLeavingInterview = false;
        isLeavingRef.current = false;
        setHasEndInterviewIntent(false);
        setEndInterviewCountdown(0);
        // 检查积分（正式用户）
        if (!isTrial && userPoints !== null && userPoints <= 0) {
            // 使用 confirm 替代 alert 以提供更好的交互（虽然还是原生弹窗）
            // 理想情况下应该使用自定义 Modal，但这里为了快速修复先用原生
            const confirmRecharge = window.confirm('您的积分不足，无法开始面试。是否前往充值？');
            if (confirmRecharge) {
                navigate('/app/pricing');
            }
            return;
        }

        // 先进入面试间界面（展示整体UI），再开始倒计时
        setShowInterviewUI(true);
        initAudio();
        // 启动倒计时，从3开始
        setCountdown(3);
    };

    // 倒计时逻辑：使用useEffect管理倒计时
    useEffect(() => {
        if (countdown === null) return;

        // 如果已经是'start'，延迟500ms后开始面试
        if (countdown === 'start') {
            playStartBeep();
            const timer = setTimeout(() => {
                actuallyStartInterview();
                setCountdown(null);
            }, 1000); // 显示"开始！"1s后进入面试
            return () => clearTimeout(timer);
        }

        // 倒计时：3 -> 2 -> 1 -> 'start'
        const timer = setTimeout(() => {
            if (countdown === 3) {
                setCountdown(2);
            } else if (countdown === 2) {
                setCountdown(1);
            } else if (countdown === 1) {
                setCountdown('start');
            }
        }, 1000); // 每秒更新一次

        return () => clearTimeout(timer);
    }, [countdown, actuallyStartInterview, playStartBeep]);

    // 播放倒计时提示音（3/2/1）
    useEffect(() => {
        if (countdown === 3 || countdown === 2 || countdown === 1) {
            initAudio();
            playTickBeep();
        }
    }, [countdown, playTickBeep]);

    // 确认卡片界面10分钟超时强制退出机制
    useEffect(() => {
        // 只在确认卡片显示时（!showInterviewUI）启动定时器
        if (showInterviewUI) {
            // 如果已经进入面试界面，清除可能存在的定时器
            if (confirmDialogTimeoutRef.current) {
                clearTimeout(confirmDialogTimeoutRef.current);
                confirmDialogTimeoutRef.current = null;
            }
            return;
        }

        confirmDialogTimeoutRef.current = setTimeout(() => {
            confirmDialogTimeoutRef.current = null;
            handleGoBack();
        }, 10 * 60 * 1000); // 10分钟 = 600000毫秒

        // 清理函数：当组件卸载或showInterviewUI变为true时清除定时器
        return () => {
            if (confirmDialogTimeoutRef.current) {
                clearTimeout(confirmDialogTimeoutRef.current);
                confirmDialogTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showInterviewUI]); // 移除 handleGoBack 依赖，避免不必要的重新执行

    // 对话消息列表
    const [messages, setMessages] = useState([]);

    // 引用聊天室底部元素，用于滚动
    const messagesEndRef = useRef(null);

    // 初始加载后，滚动到底部
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // 标记面试官是否已发送开场白
    const [started, setStarted] = useState(false);

    const ASR_MAX_SENTENCE_SILENCE_MS = 10000;

    // ============ 语音识别状态 ============
    // 识别流程: idle -> listening -> recognizing -> processing -> (回到) listening
    const [asrState, setAsrState] = useState('idle');
    const [interimTranscript, setInterimTranscript] = useState('');
    const [pendingText, setPendingText] = useState('');
    const recognizingMessageRef = useRef(null);

    // 识别文本容器自动滚动到底部，确保始终看到最新两行
    useEffect(() => {
        if (asrState === 'recognizing' && recognizingMessageRef.current) {
            const el = recognizingMessageRef.current;
            requestAnimationFrame(() => {
                try {
                    el.scrollTop = el.scrollHeight;
                } catch (e) {
                    // 容错处理，避免偶发的渲染时机问题
                }
            });
        }
    }, [interimTranscript, asrState]);

    const currentDraftSpeech = useMemo(
        () => `${pendingText || ''} ${interimTranscript || ''}`.trim(),
        [pendingText, interimTranscript]
    );

    // 计时器
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    // 精确记录开始时间戳，避免自动退出或导航时丢失时长
    const interviewStartTsRef = useRef(null);
    // 存储 evaluateInterview 函数引用，用于刷新后的评估
    const evaluateInterviewRef = useRef(null);

    // 控制摄像头/麦克风开关
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [micEnabled, setMicEnabled] = useState(true);

    // 面试官说话状态
    const [isInterviewerSpeaking, setIsInterviewerSpeaking] = useState(false);
    // 面部状态统计与提示
    const [confidentTop1Count, setConfidentTop1Count] = useState(0);
    const [nervousTop1Count, setNervousTop1Count] = useState(0);
    const [nervousThresholdCount, setNervousThresholdCount] = useState(0);
    const [lastTopEmotion, setLastTopEmotion] = useState(null);
    const [bubbleTip, setBubbleTip] = useState({ visible: false, text: '', type: 'info' });
    const bubbleCooldownRef = useRef(0);

    // ============ 静音超时处理 ============
    // 自用户最后一次发声以来的静音时长（秒）
    const [silenceSeconds, setSilenceSeconds] = useState(0);
    // none | countdown
    const [timeoutStage, setTimeoutStage] = useState('none');
    const silenceTimerRef = useRef(null);

    // ============ 面试时长上限处理 ============
    // 解析配置中的面试总时长（秒）
    const timeLimitSeconds = useMemo(() => {
        const cfg = (location.state && location.state.configData) || interviewConfig || {};
        let minutes = cfg.interview_duration;
        if (minutes == null) {
            const d = cfg.duration;
            if (typeof d === 'number' && !Number.isNaN(d)) minutes = d;
            else if (typeof d === 'string') {
                const m = parseInt(d.replace(/[^0-9]/g, ''), 10);
                if (!Number.isNaN(m)) minutes = m;
            }
        }
        if (typeof minutes === 'number' && minutes > 0) return minutes * 60;
        return null;
    }, [location.state, interviewConfig]);
    // none | countdown（倒计时提示）
    const [timeLimitStage, setTimeLimitStage] = useState('none');
    const [showThirtySecondsHint, setShowThirtySecondsHint] = useState(false);
    const [hasShownThirtySecondsHint, setHasShownThirtySecondsHint] = useState(false);

    // ============ 自动结束面试处理 ============
    // 面试结束倒计时（秒）
    const [endInterviewCountdown, setEndInterviewCountdown] = useState(0);
    // 是否检测到结束面试意图
    const [hasEndInterviewIntent, setHasEndInterviewIntent] = useState(false);
    const endInterviewTimerRef = useRef(null);

    // ============ AI参考回答处理 ============
    const [aiCopilotData, setAICopilotData] = useState({
        question: "",
        answer: ""
    });
    // 从localStorage读取用户偏好，默认true（向后兼容）
    const [showAIAnswer, setShowAIAnswer] = useState(() => {
        const saved = localStorage.getItem('aiReferenceVisible');
        return saved !== null ? saved === 'true' : true;
    });
    const [isGeneratingAIAnswer, setIsGeneratingAIAnswer] = useState(false);
    const [aiReferenceRegenCounts, setAiReferenceRegenCounts] = useState(() => ({}));
    const [lastProcessedQuestion, setLastProcessedQuestion] = useState(""); // 记录最后处理的问题
    const processedQuestionsRef = useRef(new Set()); // 使用Set来追踪已处理的问题，避免重复

    // ============ AI气泡提示处理 ============
    const [showCopilotTooltip, setShowCopilotTooltip] = useState(false);
    const copilotTooltipTimerRef = useRef(null);
    const [copilotTooltipSeq, setCopilotTooltipSeq] = useState(0);

    const triggerCopilotTooltip = useCallback(() => {
        if (copilotTooltipTimerRef.current) {
            clearTimeout(copilotTooltipTimerRef.current);
        }
        setShowCopilotTooltip(false);
        requestAnimationFrame(() => {
            setCopilotTooltipSeq(prev => prev + 1);
            setShowCopilotTooltip(true);
        });
        copilotTooltipTimerRef.current = setTimeout(() => {
            setShowCopilotTooltip(false);
        }, 3200);
    }, []);

    useEffect(() => {
        return () => {
            if (copilotTooltipTimerRef.current) {
                clearTimeout(copilotTooltipTimerRef.current);
            }
        };
    }, []);

    // ============ 体验版试用限制 ============
    const [isTrial, setIsTrial] = useState(false);
    const [showTrialLimitModal, setShowTrialLimitModal] = useState(false);
    const [showTrialWarningModal, setShowTrialWarningModal] = useState(false); // 10秒预警弹框
    const [remainingTrialSeconds, setRemainingTrialSeconds] = useState(0); // 剩余试用秒数
    const trialStartTimeRef = useRef(null);
    const initialTrialRemainingRef = useRef(null); // 保存会话开始时的初始剩余时长（当日累计消耗后的剩余时长）

    const trialTimerRef = useRef(null); // 试用时长检查定时器
    const hasShownWarningRef = useRef(false); // 是否已显示过10秒预警

    // ============ 积分监控（正式用户） ============
    const [userPoints, setUserPoints] = useState(null);
    const [showPointsLimitModal, setShowPointsLimitModal] = useState(false);
    const pointsCheckIntervalRef = useRef(null);

    // 面试会话管理状态
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [isSessionActive, setIsSessionActive] = useState(false);

    // ============ 页面刷新/关闭提示 ============
    // 处理页面刷新时的面试结束逻辑
    const handleRefreshEndInterview = async (savedData) => {
        // 刷新后组件状态可能已丢失，这里改为优先使用缓存的 recordId
        const storedRecordId = (() => {
            try {
                const v = sessionStorage.getItem('refreshEndInterviewRecordId');
                return v ? parseInt(v, 10) : null;
            } catch {
                return null;
            }
        })();
        const effectiveRecordId = currentSessionId || storedRecordId;
        if (!effectiveRecordId) return;

        try {
            // 标记退出，阻止后续回调
            isLeavingRef.current = true;
            isLeavingInterview = true;

            // 停止语音识别
            const recognition = recognitionRef.current;
            if (recognition) {
                try {
                    recognition.stop();
                } catch (e) {
                }
            }

            if (streamCleanupRef.current) {
                try {
                    streamCleanupRef.current();
                    streamCleanupRef.current = null;
                } catch (e) {
                }
            }
            if (stream) {
                try {
                    stream.getTracks().forEach(track => {
                        try {
                            track.stop();
                        } catch (e) {
                        }
                    });
                } catch (e) {
                }
            }
            if (videoRef.current && videoRef.current.srcObject) {
                try {
                    videoRef.current.srcObject = null;
                } catch (e) {
                }
            }

            // 计算面试时长：优先使用保存的时长，如果没有则基于开始时间戳计算
            let durationSeconds = 0;
            if (savedData && savedData.durationSeconds) {
                durationSeconds = savedData.durationSeconds;
            } else if (savedData && savedData.interviewStartTs) {
                durationSeconds = Math.max(0, Math.floor((Date.now() - savedData.interviewStartTs) / 1000));
            } else if (savedData && savedData.elapsedSeconds) {
                durationSeconds = Math.max(0, parseInt(savedData.elapsedSeconds || 0, 10));
            } else {
                durationSeconds = Math.max(0, parseInt(elapsedSeconds || 0, 10));
            }

            // 记录体验版用户试用时长
            // 优先使用保存的试用状态
            const isTrialUser = savedData?.isTrial !== undefined ? savedData.isTrial : isTrial;
            const trialStart = savedData?.trialStartTime || trialStartTimeRef.current;

            if (isTrialUser && trialStart && durationSeconds > 0) {
                try {
                    const userId = getCurrentUserId();
                    if (userId) {
                        const startTime = new Date(trialStart);
                        const endTime = new Date();
                        await trialUsageAPI.recordUsage({
                            feature_name: 'mock_interview',
                            session_id: effectiveRecordId,
                            start_time: startTime,
                            end_time: endTime,
                            duration_seconds: durationSeconds
                        });
                    }
                } catch (error) {
                }
            }

            // 面试结束：先持久化“用户回答”（按轮次对齐），再更新记录状态
            try {
                const { InterviewConversationAPI } = await import('../services/interviewConversationApi');
                const questions = savedData?.questions || location.state?.questions || [];
                const userMsgs = (savedData?.messages || messages || []).filter(m => m?.sender === 'user');
                // 刷新场景下只做补全用户回答的兜底，保留服务端已有的 suggested_answer
                let serverMap = {};
                try {
                    const existing = await InterviewConversationAPI.getConversation(effectiveRecordId);
                    if (existing?.success && existing?.data?.items) {
                        (existing.data.items || []).forEach((it) => {
                            if (it && typeof it.round === 'number') serverMap[it.round] = it;
                        });
                    }
                } catch (e) { }

                const items = (questions || []).map((q, idx) => {
                    const round = idx + 1;
                    const prev = serverMap[round] || {};
                    return {
                        round,
                        question: (q && (q.question || q.content)) || prev.question || `问题 ${round}`,
                        user_answer: (userMsgs?.[idx]?.text || prev.user_answer || ''),
                        // 保留服务端已有 suggested_answer；没有才为空
                        suggested_answer: prev.suggested_answer || '',
                    };
                });

                // 仅在确实需要补全时才写回，避免无意义覆盖
                const needPersist = items.some((it) => {
                    const prev = serverMap[it.round] || {};
                    return (it.user_answer || '') !== (prev.user_answer || '') ||
                        (it.question || '') !== (prev.question || '') ||
                        (it.suggested_answer || '') !== (prev.suggested_answer || '');
                });

                if (items.length > 0 && needPersist) {
                    await InterviewConversationAPI.persistConversation(effectiveRecordId, items, questions);
                }
            } catch (e) {
            }

            await InterviewRecordAPI.updateInterviewRecordRemote(effectiveRecordId, {
                duration: InterviewRecordAPI.formatDuration(durationSeconds),
                status: 'completed',
                generating: true // 标记为正在生成报告
            });

            // 完成活跃会话
            if (currentSessionId) {
                await InterviewSessionManager.completeSession(currentSessionId);
            }

            // 注意：评估（生成报告）将在页面刷新后由 useEffect 触发
            // 我们在 sessionStorage 中保存了 recordId，页面加载后会调用 evaluateInterview

            // 扣减积分（检查是否已在 beforeunload 中扣减过）
            try {
                // 检查是否已在页面卸载时通过 sendBeacon 扣减过积分
                const alreadyDeducted = sessionStorage.getItem(`points_deducted_${effectiveRecordId}`);
                if (alreadyDeducted === 'true') {
                    sessionStorage.removeItem(`points_deducted_${effectiveRecordId}`);
                } else {
                    // 使用外部用户ID，保持与积分历史查询一致
                    const userId = getCurrentUserId() || localStorage.getItem('user_id');
                    if (userId) {
                        // 优先使用保存的 session_id；没有则使用记录ID保证幂等
                        const sid =
                            (savedData && (savedData.sessionId || savedData?.interviewConfig?.session_id)) ||
                            String(effectiveRecordId);
                        await pointsAPI.consumeByDuration(userId, durationSeconds, 'mock_interview', sid, 'end_session');
                    }
                }
            } catch (e) {
            }

        } catch (error) {
        }
    };

    // 监听页面刷新/关闭事件
    useEffect(() => {
        if (showInterviewUI && isSessionActive && currentSessionId) {
            const handleBeforeUnload = (e) => {
                // 显示浏览器原生提示
                const message = '刷新页面将离开面试间，面试将结束。确定要继续吗？';
                e.preventDefault();
                e.returnValue = message;
                return message;
            };

            const handleUnload = () => {
                // 在页面卸载时，立即停止所有媒体流，释放系统资源
                try {
                    if (streamCleanupRef.current) {
                        streamCleanupRef.current();
                        streamCleanupRef.current = null;
                    }
                    if (stream) {
                        stream.getTracks().forEach(track => {
                            try {
                                track.stop();
                            } catch (e) {
                                // 忽略错误
                            }
                        });
                    }
                    if (videoRef.current && videoRef.current.srcObject) {
                        videoRef.current.srcObject = null;
                    }
                } catch (error) {
                }

                // 在页面卸载时，保存面试数据到 sessionStorage，供刷新后评估使用
                try {
                    // 计算准确的面试时长（优先基于开始时间戳）
                    const durationSeconds = interviewStartTsRef.current
                        ? Math.max(0, Math.floor((Date.now() - interviewStartTsRef.current) / 1000))
                        : Math.max(0, parseInt(elapsedSeconds || 0, 10));

                    // 保存基本标记
                    sessionStorage.setItem('refreshEndInterview', 'true');
                    sessionStorage.setItem('refreshEndInterviewRecordId', String(currentSessionId));

                    // 保存面试数据（用于评估和时长）
                    const sessionData = {
                        messages: messages,
                        interviewConfig: interviewConfig,
                        questions: location.state?.questions || [],
                        elapsedSeconds: elapsedSeconds,
                        interviewType: location.state?.interviewType || location.state?.configData?.interviewType || 'job',
                        durationSeconds: durationSeconds,
                        interviewStartTs: interviewStartTsRef.current,
                        // 保存试用状态
                        isTrial: isTrial,
                        trialStartTime: trialStartTimeRef.current,
                        // 面部状态统计
                        emotion_summary: {
                            confidence_top1_count: confidentTop1Count,
                            nervous_top1_count: nervousTop1Count
                        }
                    };
                    sessionStorage.setItem('refreshEndInterviewData', JSON.stringify(sessionData));

                    // 尝试同步清理本地会话
                    const sessions = InterviewSessionManager.getActiveSessions();
                    const filteredSessions = sessions.filter(s => s.recordId !== currentSessionId);
                    if (filteredSessions.length !== sessions.length) {
                        localStorage.setItem('active_interview_sessions', JSON.stringify(filteredSessions));
                    }
                } catch (error) {
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            window.addEventListener('unload', handleUnload);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('unload', handleUnload);
            };
        }
    }, [showInterviewUI, isSessionActive, currentSessionId, messages, interviewConfig, elapsedSeconds, location.state, isTrial]);

    // 检查是否需要处理刷新后的面试结束
    useEffect(() => {
        const shouldEndInterview = sessionStorage.getItem('refreshEndInterview');
        const recordId = sessionStorage.getItem('refreshEndInterviewRecordId');
        const savedDataStr = sessionStorage.getItem('refreshEndInterviewData');

        if (shouldEndInterview === 'true' && recordId) {
            const recordIdNum = parseInt(recordId, 10);
            let savedData = null;

            // 恢复保存的面试数据
            if (savedDataStr) {
                try {
                    savedData = JSON.parse(savedDataStr);
                } catch (error) {
                }
            }

            // 异步处理面试结束（传入保存的数据）
            handleRefreshEndInterview(savedData).then(() => {
                // 触发评估（生成报告）
                // 使用 ref 来访问 evaluateInterview 函数，避免初始化顺序问题
                // 但需要先恢复数据，因为 evaluateInterview 依赖 messages 等状态
                if (recordIdNum && evaluateInterviewRef.current && savedData) {
                    // 临时恢复数据到状态，以便评估函数使用
                    // 注意：这里我们需要直接调用评估 API，而不是通过 evaluateInterview 函数
                    // 因为 evaluateInterview 依赖组件状态，而刷新后状态是空的
                    const performEvaluation = async () => {
                        try {
                            // 构建面试记录
                            const interviewRecord = (savedData.messages || [])
                                .map(msg => `${msg.sender === 'user' ? '候选人' : '面试官'}: ${msg.text}`)
                                .join('\n');

                            if (!interviewRecord || interviewRecord.trim().length === 0) {
                                await InterviewRecordAPI.updateInterviewRecordRemote(recordIdNum, {
                                    generating: false,
                                    has_report: false,
                                    status: 'completed'
                                });
                                return;
                            }

                            // 获取面试题目
                            const interviewQuestions = savedData.questions || [];
                            const interviewType = savedData.interviewType || 'job';
                            const config = savedData.interviewConfig || {};

                            let result;
                            if (interviewType === 'custom') {
                                // 自定义面试评估
                                const customEvaluationData = {
                                    interview_topic: config.interview_topic || config.customTopics || '',
                                    specific_requirements: config.specific_requirements || config.customRequirements || '',
                                    interview_language: config.interview_language || config.language || 'chinese',
                                    interview_record: interviewRecord,
                                    interview_materials: config.interview_materials || config.customMaterialText || '',
                                    emotion_summary: savedData.emotion_summary || {
                                        confidence_top1_count: 0,
                                        nervous_top1_count: 0,
                                        nervous_threshold_count: 0
                                    }
                                };

                                const customResult = await customInterviewApi.evaluate(customEvaluationData);
                                result = {
                                    success: customResult.success,
                                    data: customResult,
                                    error: customResult.error
                                };
                            } else {
                                // 职位面试评估
                                const evaluationData = {
                                    resume_text: config.resume_text || '',
                                    job_title: config.job_title || '',
                                    company_name: config.company_name || '',
                                    interview_questions: interviewQuestions,
                                    interview_record: interviewRecord,
                                    emotion_summary: savedData.emotion_summary || {
                                        confidence_top1_count: 0,
                                        nervous_top1_count: 0,
                                        nervous_threshold_count: 0
                                    }
                                };

                                result = await InterviewEvaluationAPI.evaluateInterview(evaluationData);
                            }

                            if (result.success) {
                                const savedDuration = savedData?.durationSeconds
                                    ? InterviewRecordAPI.formatDuration(savedData.durationSeconds)
                                    : null;

                                const updateData = {
                                    score: result.data.score,
                                    generating: false,
                                    has_report: true,
                                    status: 'completed',
                                    evaluation_data: {
                                        score: result.data.score,
                                        level: result.data.level,
                                        overall_evaluation: result.data.overall_evaluation,
                                        strengths: result.data.strengths,
                                        weaknesses: result.data.weaknesses,
                                        recommendations: result.data.recommendations
                                    }
                                };

                                if (savedDuration) {
                                    updateData.duration = savedDuration;
                                }

                                await InterviewRecordAPI.updateInterviewRecordRemote(recordIdNum, updateData);
                                window.dispatchEvent(new CustomEvent('interviewCompleted', { detail: { recordId: recordIdNum } }));
                            } else {
                                await InterviewRecordAPI.updateInterviewRecordRemote(recordIdNum, {
                                    generating: false,
                                    has_report: false,
                                    status: 'completed'
                                });
                            }
                        } catch (error) {
                            await InterviewRecordAPI.updateInterviewRecordRemote(recordIdNum, {
                                generating: false,
                                has_report: false,
                                status: 'completed'
                            });
                        }
                    };

                    // 执行评估
                    performEvaluation().catch(() => {
                    });
                }

                // 清除标记
                sessionStorage.removeItem('refreshEndInterview');
                sessionStorage.removeItem('refreshEndInterviewRecordId');
                sessionStorage.removeItem('refreshEndInterviewData');

                // 跳转到面试记录页面
                navigate('/app/interviews/mock');
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);
    const [hasHandledResume, setHasHandledResume] = useState(false);

    // 自定义面试的对话会话ID（用于后端对话历史管理）
    const [customInterviewSessionId, setCustomInterviewSessionId] = useState(null);

    const messagesRef = useRef();
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const aiCopilotRef = useRef();
    useEffect(() => {
        aiCopilotRef.current = aiCopilotData;
    }, [aiCopilotData]);

    // 检查用户是否为体验版并获取积分
    useEffect(() => {
        const checkUserStatus = async () => {
            const userId = getCurrentUserId();
            if (userId) {
                const trial = await isTrialUser(userId);
                setIsTrial(trial);

                try {
                    const pointsData = await pointsAPI.getHistory(userId);
                    setUserPoints(pointsData.balance || 0);
                } catch (error) {
                }

                if (trial) {
                    try {
                        const trialStatus = await trialUsageAPI.getStatus('mock_interview');
                        setRemainingTrialSeconds(trialStatus.remaining || 0);
                    } catch (error) {
                        setRemainingTrialSeconds(TRIAL_TIME_LIMIT);
                    }
                }
            }
        };
        checkUserStatus();
    }, []);

    // 监控试用时长（体验版用户）- 单次会话限制
    useEffect(() => {
        // 如果条件不满足，清理定时器并返回
        if (!isTrial || !started || !isSessionActive || !trialStartTimeRef.current || showTrialLimitModal) {
            if (trialTimerRef.current) {
                clearInterval(trialTimerRef.current);
                trialTimerRef.current = null;
            }
            // 重置预警状态
            hasShownWarningRef.current = false;
            setShowTrialWarningModal(false);
            return;
        }

        // 使用定时器定期检查，确保及时响应
        const checkTrialLimit = async () => {
            // 如果已经显示最终弹框或没有开始时间，停止检查
            if (showTrialLimitModal || !trialStartTimeRef.current) {
                if (trialTimerRef.current) {
                    clearInterval(trialTimerRef.current);
                    trialTimerRef.current = null;
                }
                return;
            }

            // 如果还没有初始化初始剩余时长，从后端获取
            if (initialTrialRemainingRef.current === null) {
                try {
                    const userId = getCurrentUserId();
                    if (userId) {
                        const trialStatus = await trialUsageAPI.getStatus('mock_interview');
                        initialTrialRemainingRef.current = trialStatus.remaining || 0;
                        setRemainingTrialSeconds(initialTrialRemainingRef.current);
                    }
                } catch (error) {
                    // 如果获取失败，使用当前剩余时长或默认值
                    initialTrialRemainingRef.current = remainingTrialSeconds || TRIAL_TIME_LIMIT;
                }
            }

            const trialElapsed = Math.floor((Date.now() - trialStartTimeRef.current) / 1000);
            // 使用初始剩余时长减去本次会话已用时长
            const initialRemaining = initialTrialRemainingRef.current || remainingTrialSeconds || TRIAL_TIME_LIMIT;
            const remaining = Math.max(0, initialRemaining - trialElapsed);

            // 更新剩余时间（用于显示，包括预警弹框中的实时更新）
            setRemainingTrialSeconds(remaining);

            // 剩余10秒时显示预警（只显示一次）
            if (remaining <= 10 && remaining > 0 && !hasShownWarningRef.current) {
                hasShownWarningRef.current = true;
                setShowTrialWarningModal(true);
            }

            // 剩余时长用尽，显示最终弹框并退出
            if (remaining <= 0) {
                // 清除定时器
                if (trialTimerRef.current) {
                    clearInterval(trialTimerRef.current);
                    trialTimerRef.current = null;
                }
                // 关闭预警弹框（如果还开着）
                setShowTrialWarningModal(false);
                // 显示最终弹框并停止面试（先设置状态，再调用退出函数）
                setShowTrialLimitModal(true);
                // 确保在调用 handleLeaveInterview 之前，状态已更新
                // 使用 ref 调用最新的处理函数，避免闭包陷阱导致的状态陈旧问题
                setTimeout(() => {
                    if (handleLeaveInterviewRef.current) {
                        handleLeaveInterviewRef.current();
                    }
                }, 100);
            }
        };

        // 立即检查一次
        checkTrialLimit();

        // 每秒检查一次
        trialTimerRef.current = setInterval(checkTrialLimit, 1000);

        // 清理函数
        return () => {
            if (trialTimerRef.current) {
                clearInterval(trialTimerRef.current);
                trialTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTrial, started, isSessionActive, showTrialLimitModal, showTrialWarningModal]);

    // 监控积分消耗（正式用户）
    useEffect(() => {
        // 如果是体验版，或者未开始，或者会话不活跃，或者已显示积分耗尽弹框，或者是积分未加载，则跳过
        if (isTrial || !started || !isSessionActive || showPointsLimitModal || userPoints === null) {
            if (pointsCheckIntervalRef.current) {
                clearInterval(pointsCheckIntervalRef.current);
                pointsCheckIntervalRef.current = null;
            }
            return;
        }

        const checkPointsLimit = () => {
            if (showPointsLimitModal) return;

            const currentDurationSeconds = elapsedSeconds;
            // 计算当前已消耗的分钟数（向上取整），不足1分钟按1分钟算
            // 如果elapsedSeconds为0，consumedPoints为1（开始即消耗）
            const consumedPoints = currentDurationSeconds > 0 ? Math.ceil(currentDurationSeconds / 60) : 1;

            // 如果当前时长消耗的积分超过了用户拥有的积分
            // 例如：用户有1积分。
            // 0-60秒：消耗1积分。1 <= 1 (OK)
            // 61秒：消耗2积分。2 > 1 (Exceeded -> Exit)
            if (consumedPoints > userPoints) {
                if (pointsCheckIntervalRef.current) {
                    clearInterval(pointsCheckIntervalRef.current);
                    pointsCheckIntervalRef.current = null;
                }

                setShowPointsLimitModal(true);
                // 延迟触发退出，让用户看到弹框
                setTimeout(() => {
                    if (handleLeaveInterviewRef.current) {
                        handleLeaveInterviewRef.current();
                    }
                }, 100);
            }
        };

        // 立即检查一次
        checkPointsLimit();

        // 每秒检查一次
        pointsCheckIntervalRef.current = setInterval(checkPointsLimit, 1000);

        return () => {
            if (pointsCheckIntervalRef.current) {
                clearInterval(pointsCheckIntervalRef.current);
                pointsCheckIntervalRef.current = null;
            }
        };
    }, [isTrial, started, isSessionActive, showPointsLimitModal, userPoints, elapsedSeconds]);

    // 辅助函数：检查是否处于结束面试状态
    const interviewEnding = hasEndInterviewIntent || endInterviewCountdown > 0;

    // 辅助函数：检查是否可以启动语音识别
    const canStartRecognition = useCallback(
        () => showInterviewUI && micEnabled && !isInterviewerSpeaking && !interviewEnding,
        [showInterviewUI, micEnabled, isInterviewerSpeaking, interviewEnding]
    );

    // 根据 asrState 启动/停止静音计时器
    useEffect(() => {
        // 只有在AI不在说话且用户应该说话时才启动静音计时，且不在结束面试时
        if (asrState === 'listening' && canStartRecognition()) {
            if (!silenceTimerRef.current) {
                silenceTimerRef.current = setInterval(() => {
                    setSilenceSeconds((prev) => prev + 1);
                }, 1000);
            }
        } else {
            if (silenceTimerRef.current) {
                clearInterval(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            // 当AI开始说话时，重置静音计时
            if (isInterviewerSpeaking) {
                setSilenceSeconds(0);
                setTimeoutStage('none');
            }
        }

        return () => {
            if (silenceTimerRef.current) {
                clearInterval(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
        };
    }, [asrState, showInterviewUI, micEnabled, isInterviewerSpeaking, hasEndInterviewIntent, endInterviewCountdown, interviewEnding, timeoutStage, canStartRecognition]);

    // 监控静音时长，触发不同阶段逻辑
    useEffect(() => {
        // 如果AI正在说话，或者检测到结束面试意图，不进行静音检测
        if (isInterviewerSpeaking || interviewEnding) {
            return;
        }

        if (silenceSeconds >= 60 && silenceSeconds < 65 && timeoutStage === 'none') {
            // 60秒后进入倒计时阶段
            setTimeoutStage('countdown');
        } else if (silenceSeconds >= 65) {
            // 65秒后自动退出面试
            handleLeaveInterviewRef.current();
        }
    }, [silenceSeconds, isInterviewerSpeaking, hasEndInterviewIntent, endInterviewCountdown, interviewEnding, timeoutStage]);

    // 监控面试总时长，临近结束时提示并自动退出
    useEffect(() => {
        if (!timeLimitSeconds || interviewEnding) return;
        if (elapsedSeconds >= (timeLimitSeconds - 5) && elapsedSeconds < timeLimitSeconds && timeLimitStage === 'none') {
            setTimeLimitStage('countdown');
            // 进入5秒倒计时阶段时播放提示音（一次）
            initAudio();
            playLoudTickBeep();
        } else if (elapsedSeconds >= timeLimitSeconds) {
            handleLeaveInterviewRef.current();
        } else if (elapsedSeconds < (timeLimitSeconds - 5) && timeLimitStage !== 'none') {
            setTimeLimitStage('none');
        }
    }, [elapsedSeconds, timeLimitSeconds, timeLimitStage, hasEndInterviewIntent, endInterviewCountdown, playLoudTickBeep, interviewEnding]);

    useEffect(() => {
        if (!timeLimitSeconds || hasEndInterviewIntent || endInterviewCountdown > 0) return;
        const remaining = timeLimitSeconds - elapsedSeconds;
        if (remaining === 30 && !hasShownThirtySecondsHint) {
            setShowThirtySecondsHint(true);
            setHasShownThirtySecondsHint(true);
            // 30秒提示音（一次）
            initAudio();
            playLoudTickBeep();
            setTimeout(() => setShowThirtySecondsHint(false), 5000);
        }
    }, [elapsedSeconds, timeLimitSeconds, hasShownThirtySecondsHint, hasEndInterviewIntent, endInterviewCountdown, playLoudTickBeep]);

    // 监控结束面试倒计时
    useEffect(() => {
        if (endInterviewCountdown > 0) {
            // 立即停止语音识别
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                    setAsrState('paused');
                } catch (e) {
                }
            }

            if (!endInterviewTimerRef.current) {
                // 启动结束倒计时（5秒）时播放提示音（一次）
                initAudio();
                playLoudStartBeep();
                endInterviewTimerRef.current = setInterval(() => {
                    setEndInterviewCountdown((prev) => {
                        if (prev <= 1) {
                            // 倒计时结束，自动关闭面试间
                            clearInterval(endInterviewTimerRef.current);
                            endInterviewTimerRef.current = null;
                            handleLeaveInterviewRef.current();
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            }
        } else {
            if (endInterviewTimerRef.current) {
                clearInterval(endInterviewTimerRef.current);
                endInterviewTimerRef.current = null;
            }
        }

        return () => {
            if (endInterviewTimerRef.current) {
                clearInterval(endInterviewTimerRef.current);
                endInterviewTimerRef.current = null;
            }
        };
    }, [endInterviewCountdown, playLoudStartBeep]);

    useEffect(() => {
        let timer;
        if (showInterviewUI && countdown === null) {
            timer = setInterval(() => {
                setElapsedSeconds((prev) => prev + 1);
            }, 1000);
        }
        return () => timer && clearInterval(timer);
    }, [showInterviewUI, countdown]);
    useEffect(() => {
        const timer = setInterval(() => {
            if (['listening', 'recognizing', 'processing'].includes(asrState)) {
                setAsrActiveSeconds((s) => s + 1);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [asrState]);

    const formatTime = (secs) => {
        const m = String(Math.floor(secs / 60)).padStart(2, '0');
        const s = String(secs % 60).padStart(2, '0');
        return `${m}:${s}`;
    };

    // 已移除"全屏放大"功能

    // 结束面试相关状态
    const [endConfirmOpen, setEndConfirmOpen] = useState(false);

    // 稳定保存面试类型，避免导航后 location.state 丢失导致评估走错分支
    const interviewTypeRef = useRef((location.state && (location.state.interviewType || (location.state.configData && location.state.configData.interviewType))) || 'job');





    // 点击结束：先弹出确认框
    const handleEndClick = () => {
        setEndConfirmOpen(true);
    };

    // 保存面试记录函数
    const saveInterviewRecord = async (evaluationResult = null, isGenerating = false, status = 'completed') => {
        try {
            // 获取面试开始时间（从elapsedSeconds计算）
            const interviewStartTime = new Date(Date.now() - (elapsedSeconds * 1000));
            const { date, time } = InterviewRecordAPI.formatDateTime(interviewStartTime);

            // 构建面试记录数据
            const interviewRecord = {
                type: interviewConfig?.position || interviewConfig?.job_title || '模拟面试',
                // 后端字段: interview_type
                interview_type: location.state?.configData?.interviewType || 'job',
                company: interviewConfig?.company || interviewConfig?.company_name || '',
                duration: InterviewRecordAPI.formatDuration(elapsedSeconds),
                date: date,
                time: time,
                score: evaluationResult?.score || null,
                status: status, // 支持 'completed'
                hasReport: evaluationResult !== null,
                generating: isGenerating
            };

            // 保存面试记录
            const saveResult = await InterviewRecordAPI.saveInterviewRecordRemote(interviewRecord);

            if (saveResult.success) {
                // 如果状态是completed，确保立即更新面试记录列表
                if (status === 'completed') {
                    // 触发面试记录列表刷新（通过事件或直接调用）
                    window.dispatchEvent(new CustomEvent('interviewCompleted', {
                        detail: { recordId: saveResult.data.id }
                    }));
                }

                return saveResult.data;
            } else {
                throw new Error(saveResult.error);
            }

        } catch (error) {
            throw error;
        }
    };

    // 新增评估函数
    const evaluateInterview = useCallback(async (recordId) => {
        // 只要存在对话记录，就尝试发起评估。这里不再依赖 hasEndInterviewIntent，
        // 以避免自动静音退出场景下状态尚未更新导致无法调用评估 API 的问题。
        if (!recordId) {
            return;
        }

        if (messages.length === 0) {
            // 即使没有对话记录，也要确保记录状态正确
            try {
                await InterviewRecordAPI.updateInterviewRecordRemote(recordId, {
                    generating: false,
                    has_report: false,
                    status: 'completed'
                });
                window.dispatchEvent(new CustomEvent('interviewCompleted', { detail: { recordId } }));
            } catch (error) {
            }
            return;
        }

        try {
            // 构建面试记录
            const interviewRecord = messages
                .map(msg => `${msg.sender === 'user' ? '候选人' : '面试官'}: ${msg.text}`)
                .join('\n');

            // 获取面试题目：优先从会话数据中获取，如果没有则从location.state获取
            let interviewQuestions = [];
            if (currentSessionId) {
                const session = InterviewSessionManager.getActiveSession(currentSessionId);
                if (session && session.questions) {
                    interviewQuestions = session.questions;
                }
            }

            // 如果会话数据中没有题目，则从location.state获取
            if (interviewQuestions.length === 0) {
                interviewQuestions = (location.state && location.state.questions) || [];
            }

            // 使用稳定的面试类型
            const interviewType = interviewTypeRef.current || 'job';

            let result;
            if (interviewType === 'custom') {
                // 自定义面试评估
                const customConfig = (location.state && location.state.configData) || {};
                const customEvaluationData = {
                    interview_topic: customConfig.interview_topic || customConfig.customTopics || '',
                    specific_requirements: customConfig.specific_requirements || customConfig.customRequirements || '',
                    interview_language: customConfig.interview_language || customConfig.language || 'chinese',
                    interview_record: interviewRecord,
                    interview_materials: customConfig.interview_materials || customConfig.customMaterialText || '',
                    emotion_summary: {
                        confidence_top1_count: confidentTop1Count,
                        nervous_top1_count: nervousTop1Count,
                        nervous_threshold_count: nervousThresholdCount
                    }
                };

                const customResult = await customInterviewApi.evaluate(customEvaluationData);
                result = {
                    success: customResult.success,
                    data: customResult,
                    error: customResult.error
                };
            } else {
                // 职位面试评估
                const cfg = (location.state && location.state.configData) || {};
                const evaluationData = {
                    resume_text: cfg.resume_text || '',
                    job_title: cfg.job_title || '',
                    company_name: cfg.company_name || '',
                    interview_questions: interviewQuestions,
                    interview_record: interviewRecord,
                    emotion_summary: {
                        confidence_top1_count: confidentTop1Count,
                        nervous_top1_count: nervousTop1Count,
                        nervous_threshold_count: nervousThresholdCount
                    }
                };

                // 调用职位面试评估API
                result = await InterviewEvaluationAPI.evaluateInterview(evaluationData);
            }

            if (result.success) {
                // 更新面试记录，包含完整的评估数据
                await InterviewRecordAPI.updateInterviewRecordRemote(recordId, {
                    score: result.data.score,
                    generating: false,
                    has_report: true,
                    status: 'completed', // 确保状态为已完成
                    evaluation_data: {
                        score: result.data.score,
                        level: result.data.level,
                        overall_evaluation: result.data.overall_evaluation,
                        strengths: result.data.strengths,
                        weaknesses: result.data.weaknesses,
                        recommendations: result.data.recommendations
                    }
                });
                // 延迟触发事件，确保后端状态已更新
                setTimeout(() => {
                    try {
                        window.dispatchEvent(new CustomEvent('interviewCompleted', { detail: { recordId } }));
                    } catch (_) { }
                }, 500); // 延迟500ms确保后端状态已更新
            } else {
                // 更新面试记录状态
                await InterviewRecordAPI.updateInterviewRecordRemote(recordId, {
                    generating: false,
                    has_report: false,
                    status: 'completed' // 确保状态为已完成
                });
                // 延迟触发事件，确保后端状态已更新
                setTimeout(() => {
                    try {
                        window.dispatchEvent(new CustomEvent('interviewCompleted', { detail: { recordId } }));
                    } catch (_) { }
                }, 500); // 延迟500ms确保后端状态已更新
            }
        } catch (error) {
            // 更新面试记录状态
            await InterviewRecordAPI.updateInterviewRecordRemote(recordId, {
                generating: false,
                has_report: false,
                status: 'completed' // 确保状态为已完成
            });
            try {
                window.dispatchEvent(new CustomEvent('interviewCompleted', { detail: { recordId } }));
            } catch (_) { }
        }
    }, [messages, currentSessionId, location.state, interviewTypeRef, confidentTop1Count, nervousTop1Count, nervousThresholdCount]);

    // 将 evaluateInterview 函数引用存储到 ref，供其他 useEffect 使用
    useEffect(() => {
        evaluateInterviewRef.current = evaluateInterview;
    }, [evaluateInterview]);

    // 确认结束：清理并返回
    const confirmEndInterview = async () => {
        try {
            setEndConfirmOpen(false);
            await handleLeaveInterviewRef.current();
        } catch (error) {
            // TODO: 显示错误提示给用户
        }
    };

    // 取消结束
    const cancelEndInterview = () => {
        setEndConfirmOpen(false);
        setHasEndInterviewIntent(false);
        setEndInterviewCountdown(0);
        if (recognitionRef.current && canStartRecognition()) {
            try {
                recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                setAsrState('listening');
            } catch { }
        }
    };

    const handleSendClick = async () => {
        const text = currentDraftSpeech;
        if (!text) return;
        setPendingText('');
        setInterimTranscript('');
        if (sendUserMessageRef.current) {
            await sendUserMessageRef.current(text);
        }
    };



    // 动态切换摄像头
    const handleToggleCamera = async () => {
        if (cameraEnabled) {
            // 关闭摄像头：停止并移除所有 video track
            if (stream) {
                const videoTracks = stream.getVideoTracks();
                videoTracks.forEach((track) => {
                    track.stop();
                });
                const newStream = new MediaStream(stream.getAudioTracks());
                applyNewStream(newStream);
            }
            setCameraEnabled(false);
        } else {
            // 开启摄像头：请求 video 权限并合并轨道
            try {
                const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
                const videoTrack = camStream.getVideoTracks()[0];

                let newStream;
                if (stream) {
                    newStream = new MediaStream([...stream.getTracks(), videoTrack]);
                } else {
                    newStream = camStream;
                }

                applyNewStream(newStream);
                setCameraEnabled(true);
            } catch (err) {
            }
        }
    };
    // 动态切换麦克风
    const handleToggleMic = async () => {
        if (micEnabled) {
            // 关闭麦克风：完全停止并移除 audio 轨道，释放权限
            if (stream) {
                const audioTracks = stream.getAudioTracks();
                audioTracks.forEach((track) => {
                    track.stop();
                    stream.removeTrack(track);
                });
            }
            setMicEnabled(false);
        } else {
            // 开启麦克风：如果已有轨道则启用，否则尝试重新获取
            const audioTracksExisting = stream ? stream.getAudioTracks() : [];
            if (audioTracksExisting.length === 0) {
                try {
                    const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const audioTrack = audioStream.getAudioTracks()[0];
                    if (stream) {
                        stream.addTrack(audioTrack);
                    } else {
                        const newStream = new MediaStream([audioTrack]);
                        applyNewStream(newStream);
                    }
                } catch (err) {
                    return; // 保持关闭状态
                }
            }

            setMicEnabled(true);
        }
    };
    // 处理升级（记录试用并跳转）
    const handleUpgrade = async () => {
        // 防止重复调用
        if (isLeavingRef.current) {
            return;
        }

        // 标记退出
        isLeavingRef.current = true;
        isLeavingInterview = true;

        // 停止语音识别
        const recognition = recognitionRef.current;
        if (recognition) {
            try {
                recognition.stop();
                setAsrState('paused');
            } catch (e) {
            }
        }

        // 停止所有音频
        activeAudioSources.forEach(src => {
            try {
                src.onended = null;
                src.stop();
            } catch { }
        });
        activeAudioSources = [];
        try { audioCtx.suspend(); } catch { }

        // 停止视频流
        if (streamCleanupRef.current) {
            try {
                streamCleanupRef.current();
                streamCleanupRef.current = null;
            } catch (e) {
            }
        }
        // 额外检查并停止当前流的所有 tracks
        if (stream) {
            try {
                stream.getTracks().forEach(track => {
                    try {
                        track.stop();
                    } catch (e) {
                    }
                });
            } catch (e) {
            }
        }
        // 清理视频元素的媒体流引用
        if (videoRef.current && videoRef.current.srcObject) {
            try {
                videoRef.current.srcObject = null;
            } catch (e) {
            }
        }

        // 记录试用时长
        if (isTrial && trialStartTimeRef.current) {
            try {
                const userId = getCurrentUserId();
                if (userId) {
                    const startTime = new Date(trialStartTimeRef.current);
                    const endTime = new Date();
                    // 计算时长，至少记录1秒，确保计入使用次数
                    const durationSeconds = Math.max(1, Math.floor((endTime - startTime) / 1000));

                    await trialUsageAPI.recordUsage({
                        feature_name: 'mock_interview',
                        session_id: currentSessionId || `session_${Date.now()}`,
                        start_time: startTime,
                        end_time: endTime,
                        duration_seconds: durationSeconds
                    });
                }
            } catch (error) {
            }
        }

        navigate('/app/pricing');
    };

    const handleLeaveInterview = async () => {
        // 防止重复调用
        if (isLeavingRef.current) {
            return;
        }

        // 标记退出，阻止后续回调继续更新或启动TTS
        isLeavingRef.current = true;
        isLeavingInterview = true;
        // 退出前立即停止语音识别与语音合成，避免退出后仍有音频活动
        try {
            const recognition = recognitionRef.current;
            if (recognition) {
                try {
                    recognition.stop();
                    setAsrState('paused');
                } catch (e) {
                }
            }
            // 取消当前TTS，并停止所有活动音频源
            if (currentTTSAbortController) {
                try { currentTTSAbortController.abort(); } catch { }
            }
            // 中断当前聊天流
            if (currentChatAbortController) {
                try { currentChatAbortController.abort(); } catch { }
            }
            onTTSCompleteCallback = null;

            // 强制停止所有音频源并清理回调
            activeAudioSources.forEach(src => {
                try {
                    src.onended = null;
                    src.disconnect(); // 断开音频连接
                    src.stop();
                } catch { }
            });
            activeAudioSources = [];

            // 重置音频调度时间，防止已调度的音频继续播放
            scheduledTime = audioCtx.currentTime;

            // 立即暂停音频上下文，避免残留播放
            try {
                await audioCtx.suspend();
            } catch (e) {
            }

            // 立即停止所有媒体流（音视频），释放系统资源
            if (streamCleanupRef.current) {
                try {
                    streamCleanupRef.current();
                    streamCleanupRef.current = null;
                } catch (e) {
                }
            }
            // 额外检查并停止当前流的所有 tracks
            if (stream) {
                try {
                    stream.getTracks().forEach(track => {
                        try {
                            track.stop();
                        } catch (e) {
                        }
                    });
                } catch (e) {
                }
            }
            // 清理视频元素的媒体流引用
            if (videoRef.current && videoRef.current.srcObject) {
                try {
                    videoRef.current.srcObject = null;
                } catch (e) {
                }
            }
        } catch (e) {
            // 忽略清理过程中的错误
        }
        // 设置结束意图，以便触发评估
        setHasEndInterviewIntent(true);

        let recordId = currentSessionId;

        // 计算更准确的面试时长（优先基于开始时间戳）
        const durationSeconds = interviewStartTsRef.current
            ? Math.max(0, Math.floor((Date.now() - interviewStartTsRef.current) / 1000))
            : Math.max(0, parseInt(elapsedSeconds || 0, 10));

        // 记录体验版用户试用时长
        if (isTrial && trialStartTimeRef.current && durationSeconds > 0) {
            try {
                const userId = getCurrentUserId();
                if (userId) {
                    const startTime = new Date(trialStartTimeRef.current);
                    const endTime = new Date();
                    await trialUsageAPI.recordUsage({
                        feature_name: 'mock_interview',
                        session_id: currentSessionId || `session_${Date.now()}`,
                        start_time: startTime,
                        end_time: endTime,
                        duration_seconds: durationSeconds
                    });
                }
            } catch (error) {
                // 记录失败不影响用户体验，只记录日志
            }
            // 重置试用开始时间和初始剩余时长
            trialStartTimeRef.current = null;
            initialTrialRemainingRef.current = null;
        }

        try {
            if (recordId) {
                try {
                    await flushConversationToServer(recordId);
                } catch (e) {
                }

                try {
                    await InterviewRecordAPI.updateInterviewRecordRemote(recordId, {
                        duration: InterviewRecordAPI.formatDuration(durationSeconds),
                        status: 'completed',
                        generating: true
                    });
                } catch (error) {
                }
            } else {
            }

            // 完成活跃会话
            if (currentSessionId) {
                await InterviewSessionManager.completeSession(currentSessionId);
                setCurrentSessionId(null);
                setIsSessionActive(false);
            }

            // 扣减积分（按时长计费）：模拟面试会话（使用与记录一致的时长）
            // 检查是否已在页面卸载时通过 sendBeacon 扣减过积分
            const alreadyDeducted = recordId ? sessionStorage.getItem(`points_deducted_${recordId}`) : null;
            if (alreadyDeducted === 'true') {
                if (recordId) {
                    sessionStorage.removeItem(`points_deducted_${recordId}`);
                }
            } else {
                try {
                    const userStr = localStorage.getItem('user');
                    const parsed = userStr ? JSON.parse(userStr) : null;
                    const userId = parsed?.id || localStorage.getItem('user_id');
                    if (userId) {
                        // 使用记录ID作为 session_id，确保幂等
                        const sid = String(recordId);
                        const res = await pointsAPI.consumeByDuration(userId, durationSeconds, 'mock_interview', sid, 'end_session');
                        if (typeof window !== 'undefined') {
                            const change = -(res.points_deducted || 0);
                            window.dispatchEvent(new CustomEvent('pointsUpdated', {
                                detail: {
                                    change,
                                    reason: 'mock_interview',
                                    newBalance: res.balance,
                                    message: change ? `模拟面试-按时长扣减 ${Math.abs(change)} 积分` : '模拟面试-本次无积分扣减'
                                }
                            }));
                        }
                    }
                } catch (e) {
                }
            }

            // 触发评估（在页面跳转前，确保评估能正常执行）
            if (recordId) {
                // 确保评估在页面跳转前启动，但不阻塞
                evaluateInterview(recordId).catch(() => {
                });
            } else {
            }
        } catch (error) {
        }

        try {
            const sid = recordId || currentSessionId;
            if (sid) {
                // 计算总面试时长（用于记录试用时长）
                const totalDurationSeconds = interviewStartTsRef.current
                    ? Math.max(0, Math.floor((Date.now() - interviewStartTsRef.current) / 1000))
                    : Math.max(0, parseInt(elapsedSeconds || 0, 10));

                const payload = {
                    session_id: String(sid),
                    asr_duration_seconds: Math.max(0, parseInt(asrActiveSeconds || 0, 10)),
                    tts_cn_en_characters: Math.max(0, parseInt(ttsCnEnChars || 0, 10)),
                    tts_jp_kr_characters: Math.max(0, parseInt(ttsJpKrChars || 0, 10)),
                    total_duration_seconds: totalDurationSeconds  // 传递总面试时长
                };
                // 使用相对路径，通过Nginx代理访问后端API
                const url = `${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/mock-interview/session/end`;
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
                };
                fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) }).catch(() => { });
            }
        } catch (_) { }

        // 延迟返回，确保评估任务已启动（但不要太久，避免用户等待）
        setTimeout(() => {
            handleGoBack();
        }, 100);
    };
    const handleLeaveInterviewRef = useRef(handleLeaveInterview);
    handleLeaveInterviewRef.current = handleLeaveInterview;

    // 返回配置页面
    // shouldOpenConfig: true 表示用户点击"返回配置"按钮，需要打开配置弹框
    // shouldOpenConfig: false 表示正常结束面试，不需要打开配置弹框
    const handleGoBack = async (shouldOpenConfig = false) => {
        // 检查是否已经生成了面试题（题目已生成但用户未实际面试）
        // 判断条件：有题目数据 && 有配置数据 && 还没有开始面试（没有会话ID或没有消息记录）
        const hasGeneratedQuestions = location.state?.questions && location.state.questions.length > 0;
        const hasConfigData = location.state?.configData;
        const hasNotStartedInterview = !currentSessionId || messages.length === 0;

        // 如果已经生成了题目但还没有开始面试，创建一条未完成的面试记录
        if (hasGeneratedQuestions && hasConfigData && hasNotStartedInterview) {
            try {
                const configData = location.state.configData;
                const now = new Date();
                const { date, time } = InterviewRecordAPI.formatDateTime(now);

                // 构建未完成的面试记录
                const incompleteRecord = {
                    type: configData?.position || configData?.job_title || configData?.customTopics || '模拟面试',
                    interview_type: configData?.interviewType || location.state?.interviewType || 'job',
                    company: configData?.company || configData?.company_name || '-',
                    duration: '0秒', // 时长为0
                    date: date,
                    time: time,
                    score: 0, // 得分为0
                    status: 'completed', // 状态为已完成
                    hasReport: false, // 没有报告，不触发评估
                    generating: false, // 不在生成中
                    evaluation_data: null // 报告内容为空，不调用模型
                };
                // 保存未完成的面试记录
                const saveResult = await InterviewRecordAPI.saveInterviewRecordRemote(incompleteRecord);

                if (saveResult.success) {
                    // 触发面试记录列表刷新
                    window.dispatchEvent(new CustomEvent('interviewCompleted', {
                        detail: { recordId: saveResult.data.id }
                    }));
                } else {
                }
            } catch (error) {
                // 即使保存失败，也继续执行返回操作
            }
        }

        try {
            const rid = currentSessionId || parseInt(sessionStorage.getItem('refreshEndInterviewRecordId') || '', 10);
            if (rid) {
                await flushConversationToServer(rid);
            }
        } catch (e) { }

        // 标记退出，阻止回调继续执行
        isLeavingRef.current = true;
        // 停止语音识别
        const recognition = recognitionRef.current;
        if (recognition) {
            try { recognition.stop(); setAsrState('paused'); } catch { }
        }
        // 中断聊天与TTS
        if (currentChatAbortController) { try { currentChatAbortController.abort(); } catch { } }
        if (currentTTSAbortController) { try { currentTTSAbortController.abort(); } catch { } }
        onTTSCompleteCallback = null;
        // 停止所有音频源并暂停音频上下文
        activeAudioSources.forEach(src => { try { src.onended = null; src.stop(); } catch { } });
        activeAudioSources = [];
        try { audioCtx.suspend(); } catch { }
        // 停止视频流
        if (streamCleanupRef.current) {
            streamCleanupRef.current();
            streamCleanupRef.current = null;
        }
        // 返回模拟面试首页，并携带本次配置，方便在"返回配置"时自动还原之前填写的信息
        navigate('/app/interviews/mock', {
            state: {
                fromSession: true,
                shouldOpenConfig: shouldOpenConfig, // 标记是否需要打开配置弹框
                // 原始配置数据（职位、公司、描述、时长、语言等）
                configData: location.state?.configData || null,
                // 面试类型（职位 / 自定义）
                interviewType:
                    location.state?.interviewType ||
                    location.state?.configData?.interviewType ||
                    'job',
            },
        });
    };

    // 处理AI建议按钮点击 - 只负责切换显示/隐藏状态
    const handleGetAISuggestion = () => {
        const currentQuestion = getCurrentInterviewerQuestion();
        if (!currentQuestion) {
            return;
        }

        // 切换显示/隐藏状态，并保存到localStorage
        setShowAIAnswer(prev => {
            const newState = !prev;
            // 保存用户偏好到localStorage
            localStorage.setItem('aiReferenceVisible', String(newState));
            return newState;
        });
    };

    const handleRegenerateAIReference = async () => {
        const currentQuestion = getCurrentInterviewerQuestion();
        if (!currentQuestion) return;

        const used = aiReferenceRegenCounts[currentQuestion] || 0;
        if (used >= 3) return;

        // 先占位计数，避免并发/连点导致超出上限
        setAiReferenceRegenCounts(prev => ({
            ...prev,
            [currentQuestion]: (prev[currentQuestion] || 0) + 1
        }));

        await generateAIReferenceAnswer(currentQuestion);
        // 重新生成后强制展示（不改变用户偏好开关的持久化语义）
        setShowAIAnswer(true);
    };

    // 获取当前面试官的问题
    const getCurrentInterviewerQuestion = useCallback(() => {
        // 从最新的面试官消息中获取问题
        const interviewerMessages = messages
            .filter(msg => msg.sender === 'interviewer' && msg.text && msg.text.trim())
            .sort((a, b) => b.time - a.time);

        if (interviewerMessages.length > 0) {
            return interviewerMessages[0].text;
        }

        return null;
    }, [messages]);

    // 生成AI参考回答的核心函数
    const generateAIReferenceAnswer = useCallback(async (question) => {
        if (isGeneratingAIAnswer) {
            return;
        }

        // 检查是否检测到结束面试意图
        if (isLeavingInterview || endInterviewCountdown > 0) {
            setAICopilotData({
                question: question,
                answer: "再见"
            });
            // 只有在用户偏好允许自动显示时才自动显示
            const userPreference = localStorage.getItem('aiReferenceVisible');
            if (userPreference !== 'false') {
                setShowAIAnswer(true);
            }
            return;
        }

        try {
            setIsGeneratingAIAnswer(true);
            // 获取面试配置和简历信息
            const resumeText = location.state?.configData?.resume_text || location.state?.resumeText || '';
            const jobTitle = interviewConfig?.position || '';
            const companyName = interviewConfig?.company || '';
            const questions = location.state?.questions || [];
            const interviewTypeForPersist = interviewTypeRef.current || location.state?.interviewType || location.state?.configData?.interviewType || 'job';

            // 构建面试记录 - 在函数内部获取最新的messages，避免依赖messages状态
            const currentMessages = messages; // 这里会获取到最新的messages
            const interviewRecord = currentMessages
                .filter(msg => msg.text && msg.text.trim())
                .map(msg => `${msg.sender === 'interviewer' ? '面试官' : '候选人'}: ${msg.text}`)
                .join('\n');

            // 语言映射：将UI语言值映射到API期望的chinese/english/japanese/korean（已移除双语）
            const rawLang = interviewConfig?.language || location.state?.configData?.language || location.state?.language;
            const mapInterviewLanguage = (lang) => {
                if (!lang) return 'chinese';
                const l = String(lang).toLowerCase();
                if (['中文', 'chinese', 'zh', 'cn', '简体中文', '汉语', 'zh-cn'].includes(l)) return 'chinese';
                if (['英文', 'english', 'en', '英语'].includes(l)) return 'english';
                if (['日语', 'japanese', 'ja', 'jp', '日文'].includes(l)) return 'japanese';
                if (['韩语', 'korean', 'ko', 'kr', '韩文', '한국어'].includes(l)) return 'korean';
                return 'chinese';
            };
            const interviewLanguage = mapInterviewLanguage(rawLang);

            // 根据面试类型选择不同的AI参考回答API
            const interviewTypeForReference = interviewTypeRef.current || location.state?.interviewType || location.state?.configData?.interviewType || 'job';

            let response;
            if (interviewTypeForReference === 'custom') {
                // 自定义面试AI参考回答
                const customReferenceData = {
                    interview_topic: location.state?.configData?.interview_topic || location.state?.configData?.customTopics || '',
                    specific_requirements: location.state?.configData?.specific_requirements || location.state?.configData?.customRequirements || '',
                    interview_record: interviewRecord,
                    current_question: question,
                    interview_language: interviewLanguage,
                    interview_materials: location.state?.configData?.interview_materials || location.state?.configData?.customMaterialText || '（背景资料未提供）'
                };
                // 调用自定义面试AI参考回答API
                // 使用相对路径，通过Nginx代理访问后端API
                const apiResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/custom-interview/ai-reference`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
                    },
                    body: JSON.stringify(customReferenceData),
                });

                if (!apiResponse.ok) {
                    throw new Error(`自定义面试AI参考回答API调用失败: ${apiResponse.status}`);
                }

                const customResult = await apiResponse.json();
                response = {
                    answer: customResult.answer,
                    success: customResult.success
                };
            } else {
                // 职位面试AI参考回答
                response = await AIReferenceAPI.generateReferenceAnswer({
                    job_title: jobTitle,
                    company_name: companyName,
                    resume_text: resumeText,
                    interview_questions: questions, // 传递完整的题目对象，包含question和sample_answer
                    interview_record: interviewRecord,
                    current_question: question,
                    interview_language: interviewLanguage
                });
            }

            // 更新AI参考回答数据
            setAICopilotData({
                question: question,
                answer: response.answer
            });
            triggerCopilotTooltip();

            // 只有在用户偏好允许自动显示时才自动显示
            // 如果用户之前手动隐藏了，则不自动显示，只有手动点击才显示
            const userPreference = localStorage.getItem('aiReferenceVisible');
            if (userPreference !== 'false') {
                setShowAIAnswer(true);
            }
        } catch (error) {
            // 显示错误信息
            setAICopilotData({
                question: question,
                answer: `抱歉，生成参考回答时出现错误：${error.message}`
            });

            // 只有在用户偏好允许自动显示时才自动显示
            const userPreference = localStorage.getItem('aiReferenceVisible');
            if (userPreference !== 'false') {
                setShowAIAnswer(true);
            }
        } finally {
            setIsGeneratingAIAnswer(false);
        }
    }, [isGeneratingAIAnswer, interviewConfig, location.state, messages, hasEndInterviewIntent, endInterviewCountdown, triggerCopilotTooltip]);

    // 当流或界面切换时，将视频流绑定到新的video元素
    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream, showInterviewUI]);

    // 定时保存会话状态
    useEffect(() => {
        if (isSessionActive && currentSessionId) {
            const saveInterval = setInterval(async () => {
                try {
                    // 获取面试题目：优先从会话数据中获取，如果没有则从location.state获取
                    let interviewQuestions = [];
                    const session = InterviewSessionManager.getActiveSession(currentSessionId);
                    if (session && session.questions) {
                        interviewQuestions = session.questions;
                    }

                    // 如果会话数据中没有题目，则从location.state获取
                    if (interviewQuestions.length === 0) {
                        interviewQuestions = location.state?.questions || [];
                    }

                    await InterviewSessionManager.saveActiveSession({
                        recordId: currentSessionId,
                        interviewConfig: interviewConfig,
                        questions: interviewQuestions, // 添加面试题目
                        messages: messages,
                        elapsedSeconds: elapsedSeconds,
                        status: 'active'
                    });
                } catch (error) {
                }
            }, 30000);

            return () => clearInterval(saveInterval);
        }
    }, [isSessionActive, currentSessionId, messages, elapsedSeconds, interviewConfig, location.state]);

    // 统一的发送消息（供语音识别最终结果调用）
    const sendUserMessage = async (text) => {
        // 如果检测到结束面试意图或正在倒计时，直接忽略所有用户消息
        if (interviewEnding) {
            return;
        }

        if (!text) return;

        const currentTime = Date.now();

        // 防抖机制：如果在短时间内收到多个用户消息，只处理最后一个
        if (currentTime - lastUserMessageTime < USER_MESSAGE_DEBOUNCE_DELAY) {
            // 清除之前的定时器
            if (userMessageDebounceTimer) {
                clearTimeout(userMessageDebounceTimer);
            }

            // 设置新的定时器，延迟处理最新的消息
            pendingUserMessage = text;
            userMessageDebounceTimer = setTimeout(() => {
                // 再次检查是否在结束面试状态
                if (interviewEnding) {
                    pendingUserMessage = null;
                    return;
                }
                if (pendingUserMessage) {
                    processUserMessage(pendingUserMessage);
                    pendingUserMessage = null;
                }
            }, USER_MESSAGE_DEBOUNCE_DELAY);

            return;
        }

        // 更新最后处理时间
        lastUserMessageTime = currentTime;

        // 直接处理用户消息
        processUserMessage(text);
    };
    const sendUserMessageRef = useRef(sendUserMessage);
    sendUserMessageRef.current = sendUserMessage;

    // 辅助函数：将UI语言值映射到API期望的格式
    const mapInterviewLanguage = (lang) => {
        if (!lang) return 'chinese';
        const l = String(lang).toLowerCase();
        if (['中文', 'chinese', 'zh', 'cn', '简体中文', '汉语', 'zh-cn'].includes(l)) return 'chinese';
        if (['英文', 'english', 'en', '英语'].includes(l)) return 'english';
        if (['日语', 'japanese', 'ja', 'jp', '日文'].includes(l)) return 'japanese';
        if (['韩语', 'korean', 'ko', 'kr', '韩文', '한국어'].includes(l)) return 'korean';
        return 'chinese';
    };


    const flushConversationToServer = useCallback(async (recordIdOverride) => {
        const rid = recordIdOverride || currentSessionId;
        if (!rid) return;

        try {
            const { InterviewConversationAPI } = await import('../services/interviewConversationApi');
            const qs = location.state?.questions || [];
            const currentMessages = messagesRef.current || messages || [];

            const validMessages = currentMessages.filter(m =>
                m?.sender &&
                (m.sender === 'user' || m.sender === 'interviewer') &&
                m?.text &&
                String(m.text).trim()
            );

            if (validMessages.length > 0) {
                await InterviewConversationAPI.persistConversation(rid, validMessages, qs);
            }
        } catch (e) {
            console.error('保存对话消息失败:', e);
        }
    }, [currentSessionId, location.state, messages]);

    const processUserMessage = async (text) => {
        if (!text) return;

        // 直接使用原始文本，Fun-ASR API已自带标点符号
        const userMsg = { id: Date.now(), text: text, sender: 'user', time: Date.now() };

        // 先构建完整的对话历史（包含当前用户消息）
        const fullConversationHistory = [...messages, userMsg];

        // 更新UI状态
        setMessages(prev => [...prev, userMsg]);

        const botId = Date.now() + 1;
        setMessages(prev => [...prev, { id: botId, text: '', sender: 'interviewer', time: Date.now(), isStreaming: true }]);

        // 根据面试类型选择正确的action
        const interviewType = interviewTypeRef.current || location.state?.interviewType || location.state?.configData?.interviewType || 'job';
        const action = interviewType === 'custom' ? 'continue' : 'chat';

        const payload = {
            action: action,
            interview_config: { ...(interviewConfig || {}), interviewType },
            questions: location.state?.questions || [],
            conversation_history: fullConversationHistory,
            session_id: customInterviewSessionId,
        };

        let accumulated = '';

        // 立即停止语音识别，防止识别AI的声音
        const recognition = recognitionRef.current;
        if (recognition) {
            try {
                recognition.stop();
                setAsrState('paused');
            } catch (e) {
            }
        }

        // 中断上一轮聊天流，创建新的AbortController
        if (currentChatAbortController) {
            try { currentChatAbortController.abort(); } catch { }
        }
        currentChatAbortController = new AbortController();

        await InterviewChatAPI.sendMessageStream(
            payload,
            (response) => {
                // 退出后不再处理任何消息
                if (isLeavingRef.current) { return; }
                if (response.type === 'text') {
                    accumulated += response.content;
                    flushSync(() => {
                        setMessages(prev => prev.map(m => m.id === botId ? { ...m, text: accumulated, isStreaming: true } : m));
                    });
                } else if (response.type === 'tool_call' && response.tool_call) {
                    // 检测到工具调用
                    if (response.tool_call.name === 'end_interview') {
                        setHasEndInterviewIntent(true);
                        setEndInterviewCountdown(10);

                        // 立即停止语音识别，防止继续识别用户语音
                        const recognition = recognitionRef.current;
                        if (recognition) {
                            try {
                                recognition.stop();
                                setAsrState('paused');
                            } catch (e) {
                            }
                        }
                    }
                }
            },
            () => {
                // 若已退出，跳过完成回调和TTS
                if (isLeavingRef.current) { return; }
                setMessages(prev => prev.map(m => m.id === botId ? { ...m, text: accumulated, isStreaming: false } : m));

                // 如果检测到结束面试意图，立即开始倒计时
                if (hasEndInterviewIntent) {
                    setEndInterviewCountdown(5);
                }

                // 在开始新一轮 TTS 之前，清理上一轮播放相关资源，避免回调过早触发
                // 1. 取消上一次 TTS fetch
                if (currentTTSAbortController) {
                    currentTTSAbortController.abort();
                }

                // 2. 暂时清空完成回调，防止旧音频 onended 触发
                onTTSCompleteCallback = null;

                // 3. 停止并静默旧的音频节点
                activeAudioSources.forEach(src => {
                    try {
                        src.onended = null; // 防止触发回调
                        src.stop();
                    } catch (e) {
                        // 忽略已停止或错误的节点
                    }
                });
                activeAudioSources = [];

                // 4. 创建新的 TTS AbortController 并开始新的流
                currentTTSAbortController = new AbortController();

                audioCtx.resume();

                // 在启动新的 TTS 之后，定义完成回调（此时 onended 仅会被新的音频节点触发）
                onTTSCompleteCallback = () => {
                    // 如果检测到结束面试意图，不再重新启动语音识别
                    if (interviewEnding) {
                        onTTSCompleteCallback = null;
                        return;
                    }
                    setAsrState('listening');
                    // 延迟一小段时间后重新启动语音识别，确保TTS完全结束
                    setTimeout(() => {
                        if (recognitionRef.current && canStartRecognition()) {
                            try {
                                recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                            } catch (e) {
                            }
                        }
                    }, 500); // 500ms 延迟确保 TTS 完全结束
                    onTTSCompleteCallback = null;
                };

                const cfg = (location.state && location.state.configData) || interviewConfig || {};
                const rawLang = cfg.language || interviewConfig?.language || location.state?.configData?.language || location.state?.language;
                const mappedLanguage = mapInterviewLanguage(rawLang);
                // 获取面试官类型，支持多种可能的字段名
                const interviewerType = cfg.interviewerType || interviewConfig?.interviewerType || location.state?.configData?.interviewerType || location.state?.interviewerType;
                const charLen = (accumulated || '').length;
                if (mappedLanguage === 'chinese' || mappedLanguage === 'english') {
                    setTtsCnEnChars((n) => n + charLen);
                } else {
                    setTtsJpKrChars((n) => n + charLen);
                }
                TTSApi.stream(
                    accumulated,
                    appendPcmChunk,
                    () => { },
                    err => {
                        if (onTTSCompleteCallback) onTTSCompleteCallback();
                    },
                    'longyumi_v2',
                    currentTTSAbortController.signal,
                    interviewerType,
                    mappedLanguage,
                );
            },
            (err) => {
                // 退出后不再处理错误或重启识别
                if (isLeavingRef.current) { return; }
                setMessages(prev => prev.map(m => m.id === botId ? { ...m, text: `抱歉，发生错误：${err.message}`, isStreaming: false } : m));
                setAsrState('error');
                // 错误时也要重新启动语音识别（但不在结束面试时）
                if (recognitionRef.current && canStartRecognition()) {
                    try {
                        recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                        setAsrState('listening');
                    } catch (e) {
                    }
                }
            },
            currentChatAbortController.signal
        );
    };

    // 当进入面试间后，由面试官先开场
    const startInterviewAIRef = useRef(() => { });
    useEffect(() => {
        if (showInterviewUI && countdown === null && !started && interviewConfig) {
            if (!hasStartedRef.current) {
                hasStartedRef.current = true;
                startInterviewAIRef.current();
            }
        }
    }, [showInterviewUI, countdown, interviewConfig, started]);

    // 恢复面试时，让AI继续提问
    useEffect(() => {
        if (showInterviewUI && started && location.state?.resumeSession && messages.length > 0 && !hasHandledResume) {
            // 检查是否需要AI继续提问
            const lastMessage = messages[messages.length - 1];
            const isLastMessageFromUser = lastMessage && lastMessage.sender === 'user';

            if (isLastMessageFromUser) {
                // 如果最后一条消息是用户发送的，说明用户已经回答了问题，需要AI继续提问
                setHasHandledResume(true);
                // 继续面试功能已移除
            } else {
                // 如果最后一条消息是AI发送的，说明AI已经提问了，等待用户回答
                setHasHandledResume(true);
                // 启动语音识别，等待用户回答
                setTimeout(() => {
                    if (recognitionRef.current && canStartRecognition()) {
                        try {
                            recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                            setAsrState('listening');
                        } catch (e) {
                        }
                    }
                }, 500);
            }
        }
    }, [showInterviewUI, started, location.state, messages, hasHandledResume, canStartRecognition]);

    async function startInterviewAI() {
        setStarted(true);

        const botId = Date.now();
        setMessages(prev => [...prev, { id: botId, text: '', sender: 'interviewer', time: Date.now(), isStreaming: true }]);

        const payload = {
            action: 'start',
            interview_config: { ...(interviewConfig || {}), interviewType: interviewTypeRef.current || 'job' },
            questions: location.state?.questions || [],
            conversation_history: [],
            session_id: customInterviewSessionId,
        };

        let accumulated = '';

        // 立即停止语音识别，防止识别AI的声音
        const recognition = recognitionRef.current;
        if (recognition) {
            try {
                recognition.stop();
                setAsrState('paused');
            } catch (e) {
            }
        }

        // 中断上一轮聊天流，创建新的AbortController
        if (currentChatAbortController) {
            try { currentChatAbortController.abort(); } catch { }
        }
        currentChatAbortController = new AbortController();

        await InterviewChatAPI.sendMessageStream(
            payload,
            // onMessage
            (response) => {
                if (isLeavingRef.current) { return; }
                if (response.type === 'text') {
                    accumulated += response.content;
                    flushSync(() => {
                        setMessages(prev => prev.map(m => m.id === botId ? { ...m, text: accumulated, isStreaming: true } : m));
                    });
                } else if (response.type === 'tool_call' && response.tool_call) {
                    // 检测到工具调用
                    if (response.tool_call.name === 'end_interview') {
                        setHasEndInterviewIntent(true);
                    }
                }
            },
            // onComplete
            () => {
                if (isLeavingRef.current) { return; }
                setMessages(prev => prev.map(m => m.id === botId ? { ...m, text: accumulated, isStreaming: false } : m));

                // 如果检测到结束面试意图，立即开始倒计时
                if (hasEndInterviewIntent) {
                    setEndInterviewCountdown(5);
                }

                // 在开始新一轮 TTS 之前，清理上一轮播放相关资源，避免回调过早触发
                // 1. 取消上一次 TTS fetch
                if (currentTTSAbortController) {
                    currentTTSAbortController.abort();
                }

                // 2. 暂时清空完成回调，防止旧音频 onended 触发
                onTTSCompleteCallback = null;

                // 3. 停止并静默旧的音频节点
                activeAudioSources.forEach(src => {
                    try {
                        src.onended = null; // 防止触发回调
                        src.stop();
                    } catch (e) {
                        // 忽略已停止或错误的节点
                    }
                });
                activeAudioSources = [];

                // 4. 创建新的 TTS AbortController 并开始新的流
                currentTTSAbortController = new AbortController();

                audioCtx.resume();

                // 在启动新的 TTS 之后，定义完成回调（此时 onended 仅会被新的音频节点触发）
                onTTSCompleteCallback = () => {
                    // 如果检测到结束面试意图，不再重新启动语音识别
                    if (interviewEnding) {
                        onTTSCompleteCallback = null;
                        return;
                    }
                    setAsrState('listening');
                    // 延迟一小段时间后重新启动语音识别，确保TTS完全结束
                    setTimeout(() => {
                        if (recognitionRef.current && canStartRecognition()) {
                            try {
                                recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                            } catch (e) {
                            }
                        }
                    }, 500); // 500ms 延迟确保 TTS 完全结束
                    onTTSCompleteCallback = null;
                };

                const cfg = (location.state && location.state.configData) || interviewConfig || {};
                const rawLang = cfg.language || interviewConfig?.language || location.state?.configData?.language || location.state?.language;
                const mappedLanguage = mapInterviewLanguage(rawLang);
                // 获取面试官类型，支持多种可能的字段名
                const interviewerType = cfg.interviewerType || interviewConfig?.interviewerType || location.state?.configData?.interviewerType || location.state?.interviewerType;
                const charLen = (accumulated || '').length;
                if (mappedLanguage === 'chinese' || mappedLanguage === 'english') {
                    setTtsCnEnChars((n) => n + charLen);
                } else {
                    setTtsJpKrChars((n) => n + charLen);
                }
                TTSApi.stream(
                    accumulated,
                    appendPcmChunk,
                    () => { },
                    err => {
                        if (onTTSCompleteCallback) onTTSCompleteCallback();
                    },
                    'longyumi_v2',
                    currentTTSAbortController.signal,
                    interviewerType,
                    mappedLanguage,
                );
            },
            // onError
            (err) => {
                if (isLeavingRef.current) { return; }
                setMessages(prev => prev.map(m => m.id === botId ? { ...m, text: `抱歉，发生错误：${err.message}`, isStreaming: false } : m));
                // 错误时也要重新启动语音识别（但不在结束面试时）
                if (recognitionRef.current && canStartRecognition()) {
                    try {
                        recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                        setAsrState('listening');
                    } catch (e) {
                    }
                }
            },
            currentChatAbortController.signal
        );
    }

    // 更新语音状态检查逻辑
    useEffect(() => {
        const checkAudioStatus = () => {
            setIsInterviewerSpeaking(activeAudioSources.length > 0);
        };

        // 每100ms检查一次语音状态
        const intervalId = setInterval(checkAudioStatus, 100);

        return () => {
            clearInterval(intervalId);
            // 清理所有活动的音频源
            activeAudioSources.forEach(src => {
                try {
                    src.stop();
                } catch (e) {
                    // 忽略已经停止的音频源
                }
            });
            activeAudioSources = [];
        };
    }, []);

    // ============ 语音识别控制逻辑 ============
    // 语音识别实例引用
    const recognitionRef = useRef(null);
    // 标记是否已退出，避免卸载后的副作用
    const isLeavingRef = useRef(false);
    useEffect(() => {
        isLeavingInterview = false;
        isLeavingRef.current = false;
        setHasEndInterviewIntent(false);
        setEndInterviewCountdown(0);
        if (endInterviewTimerRef.current) {
            clearInterval(endInterviewTimerRef.current);
            endInterviewTimerRef.current = null;
        }
    }, []);

    // 修改语音识别结果处理，在AI说话时不处理识别结果
    const handleSpeechRecognitionResult = (event) => {
        // 如果AI正在说话，或者检测到结束面试意图，完全忽略所有识别结果
        if (isInterviewerSpeaking || asrState === 'paused' || interviewEnding) {
            return;
        }

        let interim = '';
        let finalStr = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
            const res = event.results[i];
            if (res.isFinal) {
                finalStr += res[0].transcript;
            } else {
                interim += res[0].transcript;
            }
        }

        // 处理临时结果（打字机效果）
        if (interim) {
            setInterimTranscript(interim);
            setAsrState('recognizing');
        }

        // 处理最终结果
        if (finalStr) {
            setInterimTranscript('');
            setAsrState('processing');

            // 用户已发声，重置静音计时
            setSilenceSeconds(0);
            setTimeoutStage('none');

            // final 仅作为分句标志：累积到待发送缓冲，等待用户手动点击发送
            setPendingText((prev) => {
                const p = (prev || '').trim();
                const f = (finalStr || '').trim();
                if (!f) return p;
                if (!p) return f;
                return `${p} ${f}`;
            });

            // 不自动发送，保持持续识别
            setAsrState('listening');
        }
    };

    // 彻底 stop/start 语音识别，AI说话时 stop，AI说完后 start
    useEffect(() => {
        if (!showInterviewUI || !micEnabled) return;

        // 使用语音识别服务
        const recognition = createFunAsrInterviewSpeechRecognition();
        recognitionRef.current = recognition;

        // 设置语音识别参数
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = (interviewConfig?.language === 'english') ? 'en-US' : 'zh-CN';

        // 设置事件处理器
        recognition.onstart = () => {
            setAsrState('listening');
        };

        recognition.onresult = handleSpeechRecognitionResult;

        // 有语音活动时的处理（不立即切换UI状态，等待实际识别结果）
        recognition.onactivity = () => {
            // 检测到语音活动，但不立即改变asrState
            // 只在收到实际识别结果时才更新UI，避免长时间显示"正在处理您的语音..."
        };

        recognition.onerror = (e) => {
            setAsrState('error');
            // 自动重试机制：3秒后重新启动语音识别（但不在结束面试时）
            setTimeout(() => {
                if (canStartRecognition()) {
                    try {
                        if (recognitionRef.current) {
                            recognitionRef.current.start(ASR_MAX_SENTENCE_SILENCE_MS);
                            setAsrState('listening');
                        }
                    } catch (retryError) {
                    }
                }
            }, 3000);
        };

        recognition.onend = () => {
        };

        // 不要自动 recognition.start()，只初始化。
        return () => {
            recognitionRef.current = null;
            try {
                recognition.stop();
                // 销毁语音识别实例
                if (recognition.destroy) {
                    recognition.destroy();
                }
            } catch { }
        };
    }, [showInterviewUI, micEnabled, interviewConfig, canStartRecognition]);

    // 组件卸载时清理防抖定时器
    useEffect(() => {
        return () => {
            if (userMessageDebounceTimer) {
                clearTimeout(userMessageDebounceTimer);
                userMessageDebounceTimer = null;
            }
            if (endInterviewTimerRef.current) {
                clearInterval(endInterviewTimerRef.current);
                endInterviewTimerRef.current = null;
            }
        };
    }, []);

    // 页面卸载时处理积分扣减（防止用户直接关闭页面或刷新页面）
    useEffect(() => {
        const handleBeforeUnload = async (event) => {
            // 只有在面试进行中时才需要处理
            if (isSessionActive && currentSessionId && elapsedSeconds > 0) {
                try {
                    // 使用记录ID作为稳定的 session_id，刷新后可幂等
                    const stableSessionId = String(currentSessionId || sessionStorage.getItem('refreshEndInterviewRecordId') || '');
                    const userStr = localStorage.getItem('user');
                    const parsed = userStr ? JSON.parse(userStr) : null;
                    const userId = parsed?.id || localStorage.getItem('user_id');

                    if (userId) {
                        const durationSeconds = Math.max(0, parseInt(elapsedSeconds || 0, 10));
                        const sid = stableSessionId || `mock_${Date.now()}`;

                        // 使用sendBeacon确保请求能够发送
                        const data = {
                            user_id: userId,
                            duration_seconds: durationSeconds,
                            reason: 'mock_interview',
                            session_id: sid,
                            meta: 'end_session'
                        };

                        // 使用sendBeacon发送请求，确保在页面卸载时也能发送
                        // 设置标记，防止页面刷新后重复扣减
                        sessionStorage.setItem(`points_deducted_${stableSessionId}`, 'true');

                        if (navigator.sendBeacon) {
                            // 使用相对路径，通过Nginx代理访问后端API
                            const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
                            const url = `${baseUrl}/api/v1/points/consume-by-duration`;
                            const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                            navigator.sendBeacon(url, blob);
                        } else {
                            // 降级方案：同步请求
                            // 使用相对路径，通过Nginx代理访问后端API
                            const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
                            const xhr = new XMLHttpRequest();
                            xhr.open('POST', `${baseUrl}/api/v1/points/consume-by-duration`, false); // 同步请求
                            xhr.setRequestHeader('Content-Type', 'application/json');
                            xhr.send(JSON.stringify(data));
                        }
                    }
                } catch (error) {
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isSessionActive, currentSessionId, elapsedSeconds, location.state]);

    // 自动检测面试官新问题并生成参考答案
    useEffect(() => {
        const currentQuestion = getCurrentInterviewerQuestion();

        if (currentQuestion &&
            currentQuestion !== lastProcessedQuestion &&
            !isGeneratingAIAnswer &&
            showInterviewUI &&
            !processedQuestionsRef.current.has(currentQuestion)) {

            // 检查是否正在流式生成中
            const isStreaming = messages.some(msg => msg.isStreaming);
            if (isStreaming) {
                return;
            }

            // 检查是否检测到结束面试意图
            if (isLeavingInterview || endInterviewCountdown > 0) {
                // 直接设置"再见"作为默认内容
                setAICopilotData({
                    question: currentQuestion,
                    answer: "再见"
                });
                // 只有在用户偏好允许自动显示时才自动显示
                const userPreference = localStorage.getItem('aiReferenceVisible');
                if (userPreference !== 'false') {
                    setShowAIAnswer(true);
                }
                return;
            }

            // 添加延迟，确保流式响应完全结束
            const timeoutId = setTimeout(() => {
                // 再次检查是否还在流式生成中
                const stillStreaming = messages.some(msg => msg.isStreaming);
                if (stillStreaming) {
                    return;
                }

                // 再次检查是否检测到结束面试意图
                if (isLeavingInterview || endInterviewCountdown > 0) {
                    return;
                }

                // 自动生成参考答案
                generateAIReferenceAnswer(currentQuestion);
                // 标记为已处理（放在真正开始生成之后）
                processedQuestionsRef.current.add(currentQuestion);
                setLastProcessedQuestion(currentQuestion);
            }, 500); // 500ms延迟，确保流式响应完成

            // 清理定时器
            return () => clearTimeout(timeoutId);
        }
    }, [messages, lastProcessedQuestion, isGeneratingAIAnswer, showInterviewUI, hasEndInterviewIntent, endInterviewCountdown, generateAIReferenceAnswer, getCurrentInterviewerQuestion]);

    // 面试结束时清理已处理问题记录
    useEffect(() => {
        if (hasEndInterviewIntent || endInterviewCountdown > 0) {
            processedQuestionsRef.current.clear();
            setLastProcessedQuestion("");
        }
    }, [hasEndInterviewIntent, endInterviewCountdown, interviewEnding]);

    // 面部表情数据（实时更新）
    const [expressionData, setExpressionData] = useState([]);
    const isAnalyzingRef = useRef(false); // 防止并发分析请求

    // 近期情绪历史记录（最多保存10条）
    const [emotionHistory, setEmotionHistory] = useState([]);
    const maxHistoryLength = 10;

    // 表情分析服务是否可用
    const [expressionServiceAvailable, setExpressionServiceAvailable] = useState(true);
    // 面部表情分析功能是否启用（由后端配置决定）
    const [facialExpressionEnabled, setFacialExpressionEnabled] = useState(false);

    // 检查面部表情分析功能是否启用
    useEffect(() => {
        const checkFacialExpressionStatus = async () => {
            try {
                const status = await ExpressionAnalysisAPI.checkStatus();
                setFacialExpressionEnabled(status.enabled);
                if (!status.enabled) {
                    setExpressionServiceAvailable(false);
                }
            } catch (error) {
                console.warn('检查面部表情分析功能状态失败:', error);
                setFacialExpressionEnabled(false);
                setExpressionServiceAvailable(false);
            }
        };
        checkFacialExpressionStatus();
    }, []);

    // 开启摄像头后定时捕获视频帧并进行情绪分析
    useEffect(() => {
        // 如果功能未启用，不进行面部表情分析
        if (!facialExpressionEnabled || !cameraEnabled || !videoRef.current) {
            return;
        }

        setExpressionServiceAvailable(true);

        const videoEl = videoRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let cancelled = false;
        let consecutiveFailures = 0; // 连续失败次数
        const MAX_CONSECUTIVE_FAILURES = 3; // 连续失败3次后才标记为不可用

        const captureAndAnalyze = async () => {
            if (cancelled) return;
            if (isAnalyzingRef.current) return; // 上一次分析尚未完成
            isAnalyzingRef.current = true;
            try {
                if (videoEl.readyState >= 2) { // 元数据已就绪
                    canvas.width = videoEl.videoWidth;
                    canvas.height = videoEl.videoHeight;
                    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                    const base64 = dataUrl.split(',')[1]; // 去掉 dataURL 前缀

                    const resp = await ExpressionAnalysisAPI.analyze(base64);
                    if (resp?.emotions) {
                        const newExpressions = resp.emotions.map(({ name, percent }) => ({ name, percent }));
                        setExpressionData(newExpressions);
                        setExpressionServiceAvailable(true); // 服务可用
                        consecutiveFailures = 0; // 重置失败计数

                        // 更新情绪历史记录
                        const topEmotion = newExpressions[0]; // 取概率最高的情绪
                        if (topEmotion && topEmotion.percent > 30) { // 只记录概率大于30%的情绪
                            setEmotionHistory(prev => {
                                const newHistory = [...prev, topEmotion.name];
                                // 保持最多maxHistoryLength条记录
                                return newHistory.slice(-maxHistoryLength);
                            });

                            // 仅在Top1发生变化时统计次数，避免持续同一状态导致计数爆增
                            if (topEmotion.name !== lastTopEmotion) {
                                setLastTopEmotion(topEmotion.name);
                                if (topEmotion.name === '自信') {
                                    setConfidentTop1Count(c => c + 1);
                                } else if (topEmotion.name === '紧张') {
                                    setNervousTop1Count(c => c + 1);
                                }
                            }
                        }
                        const nervousEntry = newExpressions.find(e => e.name === '紧张');
                        if (nervousEntry && nervousEntry.percent > 15) {
                            setNervousThresholdCount(c => c + 1);
                            const now = Date.now();
                            if (now - bubbleCooldownRef.current > 3000) {
                                bubbleCooldownRef.current = now;
                                setBubbleTip({
                                    visible: true,
                                    fading: false,
                                    type: 'warning',
                                    text: '检测到紧张：深呼吸两次、放慢语速、看镜头并轻微微笑'
                                });
                                setTimeout(() => {
                                    setBubbleTip(prev => ({ ...prev, fading: true }));
                                }, 2500);
                                setTimeout(() => {
                                    setBubbleTip(prev => ({ ...prev, visible: false, fading: false }));
                                }, 3300);
                            }
                        }
                    }
                }
            } catch (err) {
                consecutiveFailures++;
                // 只有连续失败多次后才标记为不可用
                if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                    setExpressionServiceAvailable(false);
                }
            } finally {
                isAnalyzingRef.current = false; // 释放锁
            }
        };

        // 每 0.5 秒分析一次，提高灵敏度
        const intervalId = setInterval(captureAndAnalyze, 500);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [cameraEnabled, facialExpressionEnabled]);

    // 取前4个最高百分比的表情数据
    const topExpressions = useMemo(() => {
        // 将相同情绪名称合并，保留最高百分比
        const merged = new Map();
        expressionData.forEach(({ name, percent }) => {
            if (!merged.has(name) || percent > merged.get(name)) {
                merged.set(name, percent);
            }
        });
        return Array.from(merged.entries())
            .map(([name, percent]) => ({ name, percent }))
            .sort((a, b) => b.percent - a.percent)
            .slice(0, 4);
    }, [expressionData]);

    // 获取面部表情对应的进度条颜色
    const getEmotionColor = (name) => {
        const colors = {
            '专注': '#6D28D9', // 紫色
            '自信': '#4F46E5', // 靛蓝
            '紧张': '#EF4444', // 红色
            '投入': '#F59E0B', // 橙色
            '愉快': '#10B981', // 绿色
            '惊讶': '#F97316', // 橙红
            '中立': '#64748B', // 灰蓝
        };
        return colors[name] || '#8B5CF6'; // 默认紫色
    };
    startInterviewAIRef.current = startInterviewAI;

    // 新增概率值样式，用于显示在进度条右侧，并随进度条长度动态定位
    // 当百分比接近100%时，确保数值不会超出容器边界
    const ProbabilityValue = styled.span`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${({ percent }) => {
            // 当百分比大于85%时，将数值显示在进度条内部，避免超出边界
            if (percent > 85) {
                return `calc(${percent}% - 35px)`;
            }
            return `calc(${percent}% + 6px)`;
        }};
    font-size: 12px;
    color: #ffffff;
    white-space: nowrap;
    z-index: 1;
  `;

    const EmotionBubble = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    max-width: 80%;
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 13px;
    background: rgba(15, 23, 42, 0.95);
    color: #ffffff;
    border: 1px solid #ef4444;
    box-shadow: 0 8px 24px rgba(2, 6, 23, 0.45);
    z-index: 20;
    pointer-events: none;
    opacity: ${({ fading }) => (fading ? 0 : 1)};
    transition: opacity 0.8s ease-out;
    will-change: opacity;
  `;

    return (
        <>
            <GlobalStyles />
            <InterviewContainer>
                {/* 倒计时覆盖层 */}
                {countdown !== null && (
                    <CountdownOverlay>
                        <CountdownNumber $isStart={countdown === 'start'}>
                            {countdown === 'start' ? '开始！' : countdown}
                        </CountdownNumber>
                    </CountdownOverlay>
                )}
                {!showInterviewUI ? (
                    <>
                        <BackButton onClick={() => handleGoBack(true)}>
                            <ArrowLeft size={20} />
                            返回配置
                        </BackButton>

                        <Content>
                            <ConfirmDialog>
                                <DialogContent>
                                    <DialogMessage>
                                        即将进入面试间，我们可以开始面试了吗
                                    </DialogMessage>
                                    <DialogHint>
                                        提示：进入面试间后，您可以通过语音与面试官对话，点击右上角视频按钮可开启视频，AI参考内容为您提供回答思路，请保持适中语速和面试官一起完成本次面试。
                                    </DialogHint>
                                    <ContinueButton onClick={handleStartInterview}>
                                        继续
                                    </ContinueButton>
                                </DialogContent>
                            </ConfirmDialog>
                        </Content>
                    </>
                ) : (
                    <InterviewLayout>
                        <VideoSection ref={videoAreaRef}>
                            {/* 全屏计时条已移除 */}
                            {false && (
                                <FullScreenTimerCard>
                                    <Clock size={32} />
                                    <span>{formatTime(timeLimitSeconds ? Math.max(timeLimitSeconds - elapsedSeconds, 0) : elapsedSeconds)}</span>
                                </FullScreenTimerCard>
                            )}

                            {/* 全屏状态信息栏已移除 */}
                            {false && (
                                <FullScreenStatusBar>
                                    {endInterviewCountdown > 0 && (
                                        <FullScreenStatusCard type="error" priority="high">
                                            <FullScreenStatusIcon>
                                                <AlertTriangle size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>面试即将结束</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>面试官已提出结束面试，将在 {endInterviewCountdown} 秒关闭面试间</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                            <FullScreenCountdownBadge>{endInterviewCountdown}s</FullScreenCountdownBadge>
                                        </FullScreenStatusCard>
                                    )}

                                    {timeoutStage === 'countdown' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="warning" priority="high">
                                            <FullScreenStatusIcon>
                                                <AlertTriangle size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>长时间未作答</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>检测到你长时间未作答，本次面试将在 {65 - silenceSeconds} 秒后结束</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                            <FullScreenCountdownBadge>{65 - silenceSeconds}s</FullScreenCountdownBadge>
                                        </FullScreenStatusCard>
                                    )}

                                    {timeLimitStage === 'countdown' && endInterviewCountdown === 0 && !hasEndInterviewIntent && timeLimitSeconds && (
                                        <FullScreenStatusCard type="warning" priority="high">
                                            <FullScreenStatusIcon>
                                                <AlertTriangle size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>面试即将结束</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>已接近规定时长，本次面试将在 {Math.max(timeLimitSeconds - elapsedSeconds, 0)} 秒后结束</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                            <FullScreenCountdownBadge>{Math.max(timeLimitSeconds - elapsedSeconds, 0)}s</FullScreenCountdownBadge>
                                        </FullScreenStatusCard>
                                    )}

                                    {asrState === 'listening' && !isInterviewerSpeaking && timeoutStage !== 'countdown' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="info" priority="normal">
                                            <FullScreenStatusIcon>
                                                <Mic size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>请讲话</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>如果您回答完毕，请点击【发送】按钮</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                            <FullScreenListeningIndicator />
                                        </FullScreenStatusCard>
                                    )}

                                    {asrState === 'recognizing' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="info" priority="normal">
                                            <FullScreenStatusIcon>
                                                <Loader size={24} className="spinning" />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>正在识别</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>{interimTranscript || '正在处理您的语音...'}</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                        </FullScreenStatusCard>
                                    )}

                                    {asrState === 'processing' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="info" priority="normal">
                                            <FullScreenStatusIcon>
                                                <Loader size={24} className="spinning" />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>正在发送</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>正在将您的回答发送给面试官</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                        </FullScreenStatusCard>
                                    )}

                                    {asrState === 'paused' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="info" priority="normal">
                                            <FullScreenStatusIcon>
                                                <CheckCircle size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>面试官正在说话</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>请稍候，面试官正在为您解答</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                        </FullScreenStatusCard>
                                    )}

                                    {asrState === 'error' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="error" priority="normal">
                                            <FullScreenStatusIcon>
                                                <XCircle size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>识别出错</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>请检查麦克风或浏览器权限设置</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                        </FullScreenStatusCard>
                                    )}

                                    {asrState === 'idle' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                        <FullScreenStatusCard type="info" priority="normal">
                                            <FullScreenStatusIcon>
                                                <CheckCircle size={24} />
                                            </FullScreenStatusIcon>
                                            <FullScreenStatusContent>
                                                <FullScreenStatusTitle>面试官正在说话</FullScreenStatusTitle>
                                                <FullScreenStatusMessage>请稍候，面试官正在为您解答</FullScreenStatusMessage>
                                            </FullScreenStatusContent>
                                        </FullScreenStatusCard>
                                    )}
                                </FullScreenStatusBar>
                            )}

                            <div style={{ display: 'flex', gap: '20px', width: '100%', justifyContent: 'center' }}>
                                <VideoContainer>
                                    {cameraEnabled ? (
                                        <UserVideo ref={videoRef} autoPlay muted playsInline />
                                    ) : (
                                        <VideoPlaceholder>
                                            <CameraOff size={48} />
                                            <span>摄像头已关闭</span>
                                        </VideoPlaceholder>
                                    )}
                                    {bubbleTip.visible && (
                                        <EmotionBubble fading={bubbleTip.fading} data-type={bubbleTip.type}>
                                            {bubbleTip.text}
                                        </EmotionBubble>
                                    )}
                                </VideoContainer>
                                <VideoContainer>
                                    <InterviewerVideo>
                                        <picture>
                                            {/* WebP格式：更好的压缩率和质量 */}
                                            <source srcSet={`${getInterviewerAvatarBase(interviewConfig?.interviewerType)}.webp`} type="image/webp" />
                                            {/* PNG格式：作为fallback，兼容不支持WebP的浏览器 */}
                                            <img src={`${getInterviewerAvatarBase(interviewConfig?.interviewerType)}.png`} alt="面试官" />
                                        </picture>
                                        <VoiceWaveform isActive={isInterviewerSpeaking} />
                                    </InterviewerVideo>
                                    <ParticipantName>面试官</ParticipantName>
                                </VideoContainer>
                            </div>

                            {/* 面部表情分析卡片 - 仅在功能启用时显示 */}
                            {facialExpressionEnabled && (
                                <>
                                    {/* 近期状态变化title */}
                                    <RecentEmotionTitle>
                                        {(() => {
                                            // 如果服务不可用，显示服务不可用提示
                                            if (!expressionServiceAvailable) {
                                                return '面部状态监测功能暂不可用';
                                            }
                                            // 如果摄像头未开启，显示提示打开摄像头
                                            if (!cameraEnabled) {
                                                return '打开摄像头开启面部状态监测';
                                            }
                                            // 如果一切正常，显示近期状态
                                            if (emotionHistory.length > 0) {
                                                return `近期状态: ${emotionHistory.slice(-3).join(' → ')}`;
                                            }
                                            // 摄像头已开启但还没有数据
                                            return '近期状态: 等待摄像头数据...';
                                        })()}
                                    </RecentEmotionTitle>
                                    <ExpressionCard>
                                        <VerticalDivider />
                                        {cameraEnabled && topExpressions.length > 0 && expressionServiceAvailable ? (
                                            // 有摄像头数据且服务可用时显示实际分析结果
                                            topExpressions.map(({ name, percent }) => (
                                                <ExpressionRow key={name}>
                                                    <EmotionName>{name}</EmotionName>
                                                    <BarContainer>
                                                        <BarFill percent={percent} color={getEmotionColor(name)} />
                                                        <ProbabilityValue percent={percent}>{`${percent.toFixed(1)}%`}</ProbabilityValue>
                                                    </BarContainer>
                                                </ExpressionRow>
                                            ))
                                        ) : (
                                            // 没有摄像头数据或服务不可用时显示初始状态
                                            <>
                                                <ExpressionRow>
                                                    <EmotionName>专注</EmotionName>
                                                    <BarContainer>
                                                        <BarFill percent={0} color={getEmotionColor('专注')} />
                                                        <ProbabilityValue percent={0}>0.0%</ProbabilityValue>
                                                    </BarContainer>
                                                </ExpressionRow>
                                                <ExpressionRow>
                                                    <EmotionName>紧张</EmotionName>
                                                    <BarContainer>
                                                        <BarFill percent={0} color={getEmotionColor('紧张')} />
                                                        <ProbabilityValue percent={0}>0.0%</ProbabilityValue>
                                                    </BarContainer>
                                                </ExpressionRow>
                                                <ExpressionRow>
                                                    <EmotionName>自信</EmotionName>
                                                    <BarContainer>
                                                        <BarFill percent={0} color={getEmotionColor('自信')} />
                                                        <ProbabilityValue percent={0}>0.0%</ProbabilityValue>
                                                    </BarContainer>
                                                </ExpressionRow>
                                                <ExpressionRow>
                                                    <EmotionName>投入</EmotionName>
                                                    <BarContainer>
                                                        <BarFill percent={0} color={getEmotionColor('投入')} />
                                                        <ProbabilityValue percent={0}>0.0%</ProbabilityValue>
                                                    </BarContainer>
                                                </ExpressionRow>
                                            </>
                                        )}
                                    </ExpressionCard>
                                </>
                            )}

                            {/* AI参考回答卡片 */}
                            <AICopilotCard>
                                {showCopilotTooltip && (
                                    <CopilotTooltip key={copilotTooltipSeq}>
                                        <Sparkles size={14} />
                                        已为您生成参考思路，可结合实际情况作答
                                    </CopilotTooltip>
                                )}
                                <AICopilotHeader>
                                    <TitleGroup>
                                         <AICopilotIcon>
                                             <Sparkles size={16} color="#3b82f6" />
                                         </AICopilotIcon>
                                         <AICopilotTitle>AI 智能参考</AICopilotTitle>
                                     </TitleGroup>
                                     <ActionGroup>
                                         <AICopilotButton onClick={handleGetAISuggestion} disabled={isGeneratingAIAnswer}>
                                             {isGeneratingAIAnswer ? (
                                                 <>
                                                     <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                                     生成中...
                                                 </>
                                             ) : (
                                                 <>
                                                     {showAIAnswer ? <EyeOff size={14} /> : <Eye size={14} />}
                                                     {showAIAnswer ? '隐藏参考' : '显示参考'}
                                                 </>
                                             )}
                                         </AICopilotButton>
                                         <AICopilotButton
                                             onClick={handleRegenerateAIReference}
                                             disabled={isGeneratingAIAnswer || !(getCurrentInterviewerQuestion()) || ((aiReferenceRegenCounts[getCurrentInterviewerQuestion()] || 0) >= 3)}
                                         >
                                             <RefreshCw size={14} style={{ animation: isGeneratingAIAnswer ? 'spin 1s linear infinite' : 'none' }} />
                                             重新生成
                                         </AICopilotButton>
                                     </ActionGroup>
                                </AICopilotHeader>
                                {showAIAnswer && (
                                    <AICopilotContent>
                                        <AICopilotTextArea>
                                            {aiCopilotData.answer}
                                        </AICopilotTextArea>
                                    </AICopilotContent>
                                )}
                            </AICopilotCard>
                            {/* 放大按钮已移除 */}
                        </VideoSection>

                        <ChatSection>
                            {/* 面试信息卡片 */}
                            <InterviewInfoCard>
                                <InfoRow>
                                    <JobInfo>
                                        <JobTitle>
                                            {(() => {
                                                const interviewType = location.state?.interviewType || location.state?.configData?.interviewType || 'job';
                                                if (interviewType === 'custom') {
                                                    return location.state?.configData?.customTopics || '自定义面试';
                                                } else {
                                                    return interviewConfig?.position || '职位';
                                                }
                                            })()}
                                        </JobTitle>
                                        {(() => {
                                            const interviewType = location.state?.interviewType || location.state?.configData?.interviewType || 'job';
                                            if (interviewType === 'custom') {
                                                return <Company>@自定义面试</Company>;
                                            } else {
                                                return interviewConfig?.company && <Company>@{interviewConfig.company}</Company>;
                                            }
                                        })()}
                                    </JobInfo>
                                    <StatusInfo>
                                        <StatusBadge status={asrState}>
                                            {asrState === 'listening' && '正在听讲'}
                                            {asrState === 'recognizing' && '正在识别'}
                                            {asrState === 'processing' && '正在发送'}
                                            {asrState === 'paused' && '面试官说话中'}
                                            {asrState === 'idle' && '等待中'}
                                            {asrState === 'error' && '识别出错'}
                                        </StatusBadge>
                                        <TimerDisplay>
                                            <Clock size={12} />
                                            <span>{formatTime(timeLimitSeconds ? Math.max(timeLimitSeconds - elapsedSeconds, 0) : elapsedSeconds)}</span>
                                        </TimerDisplay>
                                    </StatusInfo>
                                    <ControlButtons>
                                        <CircleBtn allowed={cameraEnabled} onClick={handleToggleCamera} title={cameraEnabled ? '关闭摄像头' : '开启摄像头'}>
                                            {cameraEnabled ? <Camera size={14} /> : <VideoOff size={14} />}
                                        </CircleBtn>
                                        <CircleBtn
                                            allowed={micEnabled}
                                            onClick={handleToggleMic}
                                            title={micEnabled ? '关闭麦克风' : '开启麦克风'}
                                        >
                                            {micEnabled ? <Mic size={14} /> : <MicOff size={14} />}
                                        </CircleBtn>

                                        <LeaveBtn id="end-btn" onClick={handleEndClick}>
                                            <Phone size={14} /> 结束
                                        </LeaveBtn>
                                    </ControlButtons>
                                </InfoRow>
                            </InterviewInfoCard>


                            <ChatCard>
                                <ChatHeader>
                                    <ChatTitle>对话记录</ChatTitle>
                                </ChatHeader>
                                <ChatMessages>
                                    {messages.map((message) => {
                                        let name;
                                        if (message.sender === 'interviewer') {
                                            // 检查是否为自定义面试
                                            const interviewType = location.state?.interviewType || location.state?.configData?.interviewType || 'job';
                                            if (interviewType === 'custom') {
                                                name = '面试官';
                                            } else {
                                                const personaMap = {
                                                    tech_guru_laozhang: '面试官（技术大牛·老张）',
                                                    hr_xiaomei: '面试官（HR·小美）',
                                                    boss_oldwang: '面试官（老司机·老王）',
                                                    pm_scholar_xiaoli: '面试官（学霸PM·小李）',
                                                    ceo_laoliu: '面试官（老板·老刘）',
                                                    peer_sarcastic_xiaochen: '面试官（同事·小陈）'
                                                };
                                                name = personaMap[interviewConfig?.interviewerType] || '面试官';
                                            }
                                        } else {
                                            name = '你';
                                        }
                                        const timeStr = message.time ? new Date(message.time).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) : '';
                                        return (
                                            <MessageItem key={message.id} sender={message.sender}>
                                                <MessageHeader>
                                                    <NameLabel>{name}</NameLabel>
                                                    <TimeLabel>{timeStr}</TimeLabel>
                                                </MessageHeader>
                                                {message.isStreaming && !message.text ? (
                                                    <DotsContainer style={{ alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                                                        <Dot />
                                                        <Dot />
                                                        <Dot />
                                                        <Dot />
                                                        <Dot />
                                                    </DotsContainer>
                                                ) : (
                                                    <MessageBubble sender={message.sender}>
                                                        <MessageText>{message.text}</MessageText>
                                                    </MessageBubble>
                                                )}
                                            </MessageItem>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </ChatMessages>
                            </ChatCard>

                            {currentDraftSpeech && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                <DraftSpeechBubbleOverlay
                                    title={currentDraftSpeech}
                                >
                                    {currentDraftSpeech}
                                </DraftSpeechBubbleOverlay>
                            )}

                            <StatusIndicator>
                                {endInterviewCountdown > 0 && (
                                    <StatusCardItem type="error" priority="high">
                                        <StatusIcon type="error">
                                            <AlertTriangle size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="面试即将结束">面试即将结束</StatusTitle>
                                            <StatusMessage title={`面试官已提出结束面试，将在 ${endInterviewCountdown} 秒关闭面试间`}>面试官已提出结束面试，将在 {endInterviewCountdown} 秒关闭面试间</StatusMessage>
                                        </StatusContent>
                                        <CountdownBadge>{endInterviewCountdown}s</CountdownBadge>
                                    </StatusCardItem>
                                )}

                                {timeoutStage === 'countdown' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="warning" priority="high">
                                        <StatusIcon type="warning">
                                            <AlertTriangle size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="长时间未作答">长时间未作答</StatusTitle>
                                            <StatusMessage title={`检测到你长时间未作答，本次面试将在 ${65 - silenceSeconds} 秒后结束`}>检测到你长时间未作答，本次面试将在 {65 - silenceSeconds} 秒后结束</StatusMessage>
                                        </StatusContent>
                                        <CountdownBadge>{65 - silenceSeconds}s</CountdownBadge>
                                    </StatusCardItem>
                                )}

                                {timeLimitStage === 'countdown' && endInterviewCountdown === 0 && !hasEndInterviewIntent && timeLimitSeconds && (
                                    <StatusCardItem type="warning" priority="high">
                                        <StatusIcon type="warning">
                                            <AlertTriangle size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="面试即将结束">面试即将结束</StatusTitle>
                                            <StatusMessage title={`已接近规定时长，本次面试将在 ${Math.max(timeLimitSeconds - elapsedSeconds, 0)} 秒后结束`}>已接近规定时长，本次面试将在 {Math.max(timeLimitSeconds - elapsedSeconds, 0)} 秒后结束</StatusMessage>
                                        </StatusContent>
                                        <CountdownBadge>{Math.max(timeLimitSeconds - elapsedSeconds, 0)}s</CountdownBadge>
                                    </StatusCardItem>
                                )}

                                {asrState === 'listening' && !isInterviewerSpeaking && timeoutStage !== 'countdown' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="info" priority="normal">
                                        <StatusIcon type="info">
                                            <Mic size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="请讲话">请讲话</StatusTitle>
                                            <StatusMessage title="如果您回答完毕，请点击【发送】按钮">如果您回答完毕，请点击【发送】按钮</StatusMessage>
                                        </StatusContent>
                                        <ListeningIndicatorSmall />
                                    </StatusCardItem>
                                )}

                                {asrState === 'recognizing' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="info" priority="normal">
                                        <StatusIcon type="info">
                                            <Loader size={20} className="spinning" />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="正在识别">正在识别</StatusTitle>
                                            <StatusMessage title={interimTranscript || '正在处理您的语音...'}>{interimTranscript || '正在处理您的语音...'}</StatusMessage>
                                        </StatusContent>
                                    </StatusCardItem>
                                )}

                                {asrState === 'processing' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="info" priority="normal">
                                        <StatusIcon type="info">
                                            <Loader size={20} className="spinning" />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="正在发送">正在发送</StatusTitle>
                                            <StatusMessage title="正在将您的回答发送给面试官">正在将您的回答发送给面试官</StatusMessage>
                                        </StatusContent>
                                    </StatusCardItem>
                                )}

                                {asrState === 'paused' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="info" priority="normal">
                                        <StatusIcon type="info">
                                            <CheckCircle size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="面试官正在说话">面试官正在说话</StatusTitle>
                                            <StatusMessage title="请稍候，面试官正在为您解答">请稍候，面试官正在为您解答</StatusMessage>
                                        </StatusContent>
                                    </StatusCardItem>
                                )}

                                {asrState === 'error' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="error" priority="normal">
                                        <StatusIcon type="error">
                                            <XCircle size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="识别出错">识别出错</StatusTitle>
                                            <StatusMessage title="请检查麦克风或浏览器权限设置">请检查麦克风或浏览器权限设置</StatusMessage>
                                        </StatusContent>
                                    </StatusCardItem>
                                )}

                                {asrState === 'idle' && endInterviewCountdown === 0 && !hasEndInterviewIntent && (
                                    <StatusCardItem type="info" priority="normal">
                                        <StatusIcon type="info">
                                            <CheckCircle size={20} />
                                        </StatusIcon>
                                        <StatusContent>
                                            <StatusTitle title="面试官正在说话">面试官正在说话</StatusTitle>
                                            <StatusMessage title="请稍候，面试官正在为您解答">请稍候，面试官正在为您解答</StatusMessage>
                                        </StatusContent>
                                    </StatusCardItem>
                                )}

                                <FloatingSendBtn
                                    id="send-btn"
                                    onClick={handleSendClick}
                                    disabled={!currentDraftSpeech || asrState === 'paused' || isInterviewerSpeaking}
                                >
                                    <Send size={16} /> 发送
                                </FloatingSendBtn>
                            </StatusIndicator>

                        </ChatSection>
                    </InterviewLayout>
                )}
                {endConfirmOpen && (
                    <EndDialogOverlay>
                        <EndDialog>
                            <h3>结束面试</h3>
                            <p>确定要结束此次面试吗？此操作无法撤销。</p>
                            <EndDialogActions>
                                <CancelBtn onClick={cancelEndInterview}>取消</CancelBtn>
                                <EndBtn onClick={confirmEndInterview}>确定</EndBtn>
                            </EndDialogActions>
                        </EndDialog>
                    </EndDialogOverlay>
                )}
            </InterviewContainer>

            {/* 积分耗尽限制弹框 */}
            <TrialLimitModal
                isOpen={showPointsLimitModal}
                onClose={() => { }}
                isDisabled={true}
                customMessage="您的积分已耗尽，本次面试已自动结束。请充值积分以继续使用。"
                onUpgrade={() => navigate('/app/pricing')}
            />

            {/* 10秒预警弹框 */}
            <TrialLimitModal
                isOpen={showTrialWarningModal}
                onClose={() => setShowTrialWarningModal(false)}
                featureName="模拟面试"
                isWarning={true}
                remainingSeconds={remainingTrialSeconds}
                onUpgrade={handleUpgrade}
            />

            {/* 最终限制弹框 */}
            <TrialLimitModal
                isOpen={showTrialLimitModal}
                onClose={() => setShowTrialLimitModal(false)}
                featureName="模拟面试"
                isWarning={false}
                onUpgrade={handleUpgrade}
            />
        </>
    );
};

// 自定义设备弹框相关样式组件已全部移除

// ============ 样式组件 ============
const InterviewContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, rgba(44, 44, 44, 0.9) 0%, rgba(58, 58, 58, 0.85) 100%);
  position: relative;
  overflow: hidden;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const Content = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  padding: 40px;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  text-align: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const DialogMessage = styled.p`
  font-size: 18px;
  color: #333;
  line-height: 1.5;
  margin: 0;
`;

const DialogHint = styled.p`
  font-size: 12px;
  color: #999;
  line-height: 1.4;
  margin: 0;
  text-align: justify;
  max-width: 320px;
`;

const ContinueButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #555;
  }
`;

// 倒计时覆盖层
const CountdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// 倒计时数字
const CountdownNumber = styled.div`
  font-size: ${props => props.$isStart ? '120px' : '180px'};
  font-weight: 900;
  color: ${props => props.$isStart ? '#4ade80' : '#ffffff'};
  text-align: center;
  user-select: none;
  animation: ${props => {
        if (props.$isStart) {
            return 'pulse 0.5s ease';
        }
        return 'scaleIn 0.5s ease';
    }};
  
  @keyframes scaleIn {
    0% {
      transform: scale(0.3);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
  
  text-shadow: ${props => props.$isStart
        ? '0 0 40px rgba(74, 222, 128, 0.8), 0 0 80px rgba(74, 222, 128, 0.5)'
        : '0 0 40px rgba(255, 255, 255, 0.8), 0 0 80px rgba(255, 255, 255, 0.5)'
    };
  
  letter-spacing: ${props => props.$isStart ? '8px' : '0'};
`;

// 新增的样式组件
const InterviewLayout = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  background: #1a1a1a;
  overflow: hidden;
  position: relative; /* 允许内部绝对定位元素（如全屏计时卡片）*/
  max-width: 100vw; /* 确保不超出视口宽度 */
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
`;

const VideoSection = styled.div`
  position: relative; /* 使内部 absolute 按钮相对于此容器定位 */
  flex: 0 0 65%; /* 减少视频区域宽度 */
  display: flex;
  padding: 20px;
  gap: 20px;
  background: #1a1a1a;
  align-items: flex-start; /* 改为顶部对齐 */
  justify-content: center;
  min-height: 100vh;
  padding-top: 20px; /* 移除顶部工具栏的空间 */
  overflow: hidden; /* 防止内容溢出 */
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-height: auto;
    height: auto;
    padding: 12px;
    gap: 12px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: calc(50% - 10px);
  height: 35vh; /* 减小高度，让视频更紧凑 */
  background: #2a2a2a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    height: 220px;
  }
`;

const UserVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #1a1a1a;
  transform: scaleX(-1); // 镜像显示，更自然
`;

const InterviewerVideo = styled.div`
  width: 100%;
  height: 100%;
  background: #3a3a3a;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: none;
    box-shadow: none;
    transition: transform 0.3s ease;
    position: absolute;
    top: 0;
    left: 0;
    
    &:hover {
      transform: scale(1.02);
    }
  }
`;

const ParticipantName = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  color: white;
  font-size: 16px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ChatSection = styled.div`
  flex: 0 0 35%; /* 增加聊天区域宽度 */
  position: relative;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  border-left: 1px solid #e0e0e0;
  overflow: hidden; /* 防止内容溢出 */
  box-sizing: border-box; /* 确保边距计算正确 */
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
    border-left: none;
    border-top: 1px solid #e0e0e0;
  }
`;

// 面试信息卡片样式
const InterviewInfoCard = styled.div`
  background: white;
  border-radius: 8px; /* 减小圆角 */
  margin: 12px 12px 6px 12px; /* 减小下边距 */
  padding: 16px; /* 减小内边距 */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); /* 减小阴影 */
  border: 1px solid #e9ecef;
  min-width: 320px;
  max-width: calc(100% - 24px); /* 确保不超出容器，减去左右边距 */
  overflow: hidden; /* 防止内容溢出 */
  box-sizing: border-box; /* 确保边距计算正确 */
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center; /* 改为居中对齐 */
  gap: 16px; /* 增加间距 */
  justify-content: space-between;
`;

const JobInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px; /* 减小间距 */
  flex: 1; /* 让职位信息占据更多空间 */
  min-width: 0; /* 允许收缩 */
`;

// 重新定义JobTitle和Company样式
const JobTitle = styled.span`
  font-weight: 700;
  font-size: 16px; /* 减小字号 */
  color: #101010;
  line-height: 1.2;
  white-space: nowrap; /* 防止换行 */
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Company = styled.span`
  font-weight: 500;
  color: #555;
  font-size: 13px; /* 减小字号 */
  line-height: 1.2;
  white-space: nowrap; /* 防止换行 */
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center; /* 改为水平布局 */
  gap: 8px; /* 减小间距 */
  flex-shrink: 0; /* 防止压缩 */
`;

const StatusBadge = styled.div`
  padding: 4px 10px; /* 减小内边距 */
  border-radius: 12px; /* 减小圆角 */
  font-size: 11px; /* 减小字号 */
  font-weight: 500;
  background: ${props => {
        switch (props.status) {
            case 'listening':
                return '#e3f2fd';
            case 'recognizing':
                return '#fff3e0';
            case 'processing':
                return '#e8f5e8';
            case 'paused':
            case 'idle':
                return '#f3e5f5';
            case 'error':
                return '#ffebee';
            default:
                return '#f5f5f5';
        }
    }};
  color: ${props => {
        switch (props.status) {
            case 'listening':
                return '#1976d2';
            case 'recognizing':
                return '#f57c00';
            case 'processing':
                return '#388e3c';
            case 'paused':
            case 'idle':
                return '#7b1fa2';
            case 'error':
                return '#d32f2f';
            default:
                return '#757575';
        }
    }};
  white-space: nowrap; /* 防止文字换行 */
`;

const TimerDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px; /* 减小间距 */
  padding: 4px 8px; /* 减小内边距 */
  background: #f8f9fa;
  border-radius: 10px; /* 减小圆角 */
  font-size: 11px; /* 减小字号 */
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap; /* 防止文字换行 */
  /* 使用等宽数字以避免因数字宽度差异导致的布局抖动 */
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1, 'lnum' 1;

  /* 固定时间文本的可视宽度，进一步消除抖动 */
  span {
    display: inline-block;
    min-width: 6ch; /* 约等于 mm:ss 的宽度 */
    text-align: center;
  }
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; /* 减小按钮间距 */
  flex-shrink: 0; /* 防止压缩 */
`;

const FloatingSendBtn = styled.button`
  position: static;
  z-index: 1;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 12px 16px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.18);
  flex-shrink: 0;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

// 对话记录标题样式
const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0; /* 防止标题被压缩 */
`;

const ChatTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  line-height: 1.2;
`;



const ChatCard = styled.div`
  background: white;
  border-radius: 8px;
  margin: 6px 12px 12px 12px; /* 减小上边距 */
  padding: 20px; /* 增加内边距，为标题留出空间 */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  flex: 1; /* 让卡片占据剩余空间 */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 防止内容溢出 */
  max-width: calc(100% - 24px); /* 确保不超出容器，减去左右边距 */
  box-sizing: border-box; /* 确保边距计算正确 */
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 8px 0 0; /* 添加右侧内边距，为滚动条留出空间 */
  min-height: 0; /* 确保flex子元素可以正确收缩 */
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const MessageItem = styled.div`  display: flex;
  flex-direction: column;
  align-items: ${props => props.sender === 'user' ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div`
  max-width: ${props => props.sender === 'user' ? '80%' : '90%'};
  align-self: ${props => props.sender === 'user' ? 'flex-end' : 'flex-start'};
  background: ${props => props.sender === 'user' ? '#1a1a1a' : '#ffffff'};
  color: ${props => props.sender === 'user' ? '#ffffff' : '#333333'};
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  word-wrap: break-word; /* 确保长单词能够换行 */
  overflow-wrap: break-word; /* 现代浏览器的换行属性 */
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const NameLabel = styled.span`
  font-size: 12px;
  color: #666;
`;

const TimeLabel = styled.span`
  font-size: 12px;
  color: #999;
`;

const MessageText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  text-align: justify; /* 两端对齐 */
  word-wrap: break-word; /* 长单词换行 */
  word-break: break-word; /* 确保长单词能够换行 */
  white-space: pre-wrap; /* 保留换行符和空格 */
`;

// Animated dots loader for streaming messages (when text is not yet available)
const pulse = keyframes`
  0% {
    transform: scale(0.8);
    background-color: #b3d4fc;
    box-shadow: 0 0 0 0 rgba(178, 212, 252, 0.7);
  }
  50% {
    transform: scale(1.2);
    background-color: #6793fb;
    box-shadow: 0 0 0 6px rgba(178, 212, 252, 0);
  }
  100% {
    transform: scale(0.8);
    background-color: #b3d4fc;
    box-shadow: 0 0 0 0 rgba(178, 212, 252, 0.7);
  }
`;

const DotsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin-top: 6px; /* 与姓名区域保持间距 */
`;

const Dot = styled.div`
  height: 6px;
  width: 6px;
  margin-right: 3px;
  border-radius: 50%;
  background-color: #b3d4fc;
  animation: ${pulse} 1.5s infinite ease-in-out;

  &:last-child {
    margin-right: 0;
  }

  &:nth-child(1) {
    animation-delay: -0.3s;
  }
  &:nth-child(2) {
    animation-delay: -0.1s;
  }
  &:nth-child(3) {
    animation-delay: 0.1s;
  }
`;

// 移除未使用的ChatInputSection样式

// 动画定义
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const countdownPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const wave = keyframes`
  0%, 100% { transform: scaleY(0.5); }
  50% { transform: scaleY(1); }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 状态指示器容器
const StatusIndicator = styled.div`
  width: calc(100% - 24px); /* 减去左右边距，确保与ChatCard对齐 */
  min-height: 60px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin: 12px 12px 12px 12px; /* 放到底部后，与上方内容拉开距离 */
  overflow: hidden; /* 防止内容溢出 */
  box-sizing: border-box; /* 确保边距计算正确 */
  align-items: center;
`;

const DraftSpeechBubbleOverlay = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 86px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: rgba(239, 246, 255, 0.78);
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
  max-height: 72px;
  overflow-y: auto;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
  box-sizing: border-box;
  z-index: 6;
  pointer-events: none;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.5);
    border-radius: 999px;
  }
`;

// 状态卡片
// 移除StatusCard样式，因为已删除外部卡片包装

// 状态卡片样式
const StatusCardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  flex: 1;
  min-width: 0;
  background: ${props => {
        switch (props.type) {
            case 'error':
                return props.priority === 'high' ? '#fef2f2' : '#fef7f7';
            case 'warning':
                return props.priority === 'high' ? '#fffbeb' : '#fefbf3';
            case 'info':
            default:
                return '#f8fafc';
        }
    }};
  border: 1px solid ${props => {
        switch (props.type) {
            case 'error':
                return props.priority === 'high' ? '#fecaca' : '#fed7d7';
            case 'warning':
                return props.priority === 'high' ? '#fde68a' : '#fef3c7';
            case 'info':
            default:
                return '#e2e8f0';
        }
    }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  animation: ${slideIn} 0.3s ease-out;
  position: relative;
  max-width: 100%; /* 确保不超出容器宽度 */
  overflow: hidden; /* 防止内容溢出 */
  box-sizing: border-box; /* 确保边距计算正确 */

  ${props => props.priority === 'high' && `
    background: ${props.type === 'error' ? '#fef2f2' : '#fffbeb'};
    border-color: ${props.type === 'error' ? '#fecaca' : '#fde68a'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  `}
`;

// 状态图标
const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => {
        switch (props.type) {
            case 'error':
                return '#fef2f2';
            case 'warning':
                return '#fffbeb';
            case 'info':
            default:
                return '#eff6ff';
        }
    }};
  color: ${props => {
        switch (props.type) {
            case 'error':
                return '#dc2626';
            case 'warning':
                return '#d97706';
            case 'info':
            default:
                return '#2563eb';
        }
    }};
  flex-shrink: 0;

  .spinning {
    animation: ${spin} 1s linear infinite;
  }
`;

// 状态内容
const StatusContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden; /* 防止文本溢出 */
`;

// 状态标题
const StatusTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// 状态消息
const StatusMessage = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// 仅用于“正在识别”状态：两行高、可滚动，显示最新内容
const RecognizingMessage = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-wrap;
  max-height: 2.8em; /* 两行高度（1.4 * 2） */
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px; /* 避免滚动条遮挡文字 */

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
    margin: 2px 0;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// 倒计时徽章
const CountdownBadge = styled.div`
  background: #ef4444;
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  min-width: 32px;
  text-align: center;
  animation: ${countdownPulse} 1s ease-in-out infinite;
`;

// 听音指示器
const ListeningIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 20px;

  &::before,
  &::after {
    content: '';
    width: 3px;
    height: 20px;
    background: #3b82f6;
    border-radius: 2px;
    animation: ${wave} 1s ease-in-out infinite;
  }

  &::before {
    animation-delay: -0.2s;
  }

  &::after {
    animation-delay: -0.4s;
  }
`;

const ListeningIndicatorSmall = styled(ListeningIndicator)`
  height: 16px;

  &::before,
  &::after {
    height: 16px;
  }
`;



// 移除旧的顶部工具栏样式，因为已经移到右侧卡片中

/* 全屏模式计时卡片 */
const FullScreenTimerCard = styled.div`
  position: absolute;
  top: 20px; /* 调整位置，因为不再有顶部工具栏 */
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 48px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  font-size: 36px; /* 更大字号 */
  font-weight: 700;
  letter-spacing: 1px;
  color: #ffffff;
  z-index: 60;
`;

const FullScreenStatusBar = styled.div`
  position: absolute;
  bottom: 120px; /* 距离底部120px，在屏幕正中间正下方 */
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50; /* 确保在视频上方 */
`;

const FullScreenStatusCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 400px;
  max-width: 600px;
  z-index: 1;
`;

const FullScreenStatusIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .spinning {
    animation: ${spin} 1s linear infinite;
  }
`;

const FullScreenStatusContent = styled.div`
  flex: 1;
  min-width: 0;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const FullScreenStatusTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
  line-height: 1.4;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const FullScreenStatusMessage = styled.div`
  font-size: 16px;
  color: #e0e0e0;
  line-height: 1.4;
  word-break: break-word;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const FullScreenCountdownBadge = styled.div`
  background: #ff4d4f;
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  min-width: 48px;
  text-align: center;
  animation: ${countdownPulse} 1s ease-in-out infinite;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const FullScreenListeningIndicator = styled(ListeningIndicator)`
  &::before,
  &::after {
    background: #40a9ff;
  }
`;

// 移除RightGroup样式，因为已经移到右侧卡片中

const CircleBtn = styled.button`
  width: 32px; /* 减小按钮尺寸 */
  height: 32px; /* 减小按钮尺寸 */
  border-radius: 50%;
  border: 1px solid ${(props) => (props.allowed ? '#ffb347' : '#e0e0e0')};
  background: ${(props) => (props.allowed ? '#ffb347' : '#ffffff')};
  color: ${(props) => (props.allowed ? '#ffffff' : '#333333')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.25s ease;

  &:hover {
    background: ${(props) => (props.allowed ? '#ffc57c' : '#f5f5f5')};
  }
`;

const SendBtn = styled.button`
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const LeaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  background: #e50055;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #d0004b;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #2a2a2a;
  border-radius: 12px;
  color: #888;
  font-size: 16px;
  gap: 10px;
`;

// 下拉菜单样式


// AI参考回答卡片样式
const fadeOutAnimation = keyframes`
  0% { opacity: 0; transform: translate(-50%, 6px); }
  12% { opacity: 1; transform: translate(-50%, 0); }
  78% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -8px); }
`;

const CopilotTooltip = styled.div`
  position: absolute;
  top: -38px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.35);
  pointer-events: none;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  animation: ${fadeOutAnimation} 3.2s forwards;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 4px 4px 0;
    border-style: solid;
    border-color: #2563eb transparent transparent transparent;
  }
`;

const AICopilotCard = styled.div`
  position: absolute;
  bottom: 16px;
  left: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(229, 231, 235, 0.8);
  z-index: 10;
  overflow: visible;
  transition: all 0.3s ease;
`;

const AICopilotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: transparent;
  border-bottom: 1px solid rgba(243, 244, 246, 0.8);
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AICopilotTitle = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: #1f2937;
  letter-spacing: 0.3px;
`;

const AICopilotIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 6px;
`;

const AICopilotButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f1f5f9;
    border-color: #e2e8f0;
  }
`;

const AICopilotContent = styled.div`
  padding: 4px 16px 14px;
  background: transparent;
  animation: slideDown 0.3s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      max-height: 250px;
      transform: translateY(0);
    }
  }
`;

const AICopilotTextArea = styled.div`
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 14px;
  color: #334155;
  line-height: 1.6;
  min-height: 44px;
  max-height: 140px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: system-ui, -apple-system, sans-serif;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

// 结束确认弹窗
const EndDialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const EndDialog = styled.div`
  width: 420px;
  background: #ffffff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden; // 保持圆角效果

  h3 {
    margin: 0 0 12px;
    font-size: 20px;
    color: #111;
  }

  p {
    font-size: 14px;
    color: #555;
    margin: 0 0 24px;
  }
`;

const EndDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelBtn = styled.button`
  padding: 10px 24px;
  border: 1px solid #e0e0e0;
  background: #ffffff;
  color: #333;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const EndBtn = styled.button`
  padding: 10px 24px;
  border: none;
  background: #e50055;
  color: #ffffff;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #d0004b;
  }
`;

// 全屏按钮已移除

// 添加CSS动画


// 全局样式
const GlobalStyles = styled.div`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// ============ 面部表情分析卡片样式 ============

const RecentEmotionTitle = styled.div`
  position: absolute;
  top: calc(20px + 35vh + 16px); /* 位于摄像头区域下方，20px(padding-top) + 35vh(摄像头高度) + 16px(间距，确保文本完全在区域外) */
  left: 44px; /* 与ExpressionCard内容左对齐：20px(卡片left) + 24px(卡片padding-left) = 44px，与"专注"等文字完全对齐 */
  width: 350px; /* 与ExpressionCard宽度保持一致 */
  color: #ffffff;
  font-size: 14px; /* 与EmotionName字体大小一致，确保对齐 */
  font-weight: 500;
  text-align: left; /* 改为左对齐 */
  margin: 0; /* 移除默认margin */
  padding: 0; /* 移除默认padding */
  box-sizing: border-box; /* 确保盒模型一致 */
  letter-spacing: 0; /* 确保字符间距一致 */
  line-height: 1.4; /* 与EmotionName行高一致 */
  font-family: inherit; /* 确保使用相同的字体族 */
  z-index: 12;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8); /* 添加文字阴影提高可读性 */
  transition: opacity 0.3s ease; /* 添加过渡动画 */
  
  /* 响应式设计 */
  @media (max-width: 768px) {
    width: calc(100% - 88px);
    max-width: 350px;
  }
`;

const ExpressionCard = styled.div`
  position: absolute;
  top: calc(20px + 35vh + 16px + 32px); /* 标题下方，增加间距到32px，让空白更大 */
  left: 20px;
  width: 350px; /* 增加宽度从280px到350px */
  background: rgba(15, 23, 42, 0.9); /* 深色半透明背景 */
  border: 1px solid #334155;
  border-radius: 6px; /* 减少圆角，更方的外观 */
  padding: 20px 24px; /* 增加内边距 */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  color: #ffffff;
  z-index: 11;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.3s ease; /* 添加过渡动画 */
  
  /* 悬停效果 */
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  }
  
  /* 响应式设计 */
  @media (max-width: 768px) {
    width: calc(100% - 40px);
    max-width: 350px;
    left: 20px;
    padding: 16px 20px; /* 在小屏幕上减少内边距 */
  }
`;

const ExpressionRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  transition: opacity 0.2s ease; /* 添加过渡动画 */

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmotionName = styled.span`
  flex: 0 0 60px; /* 固定左侧文字宽度 */
  font-size: 14px;
  line-height: 1.4; /* 确保行高一致 */
  font-family: inherit; /* 确保使用相同的字体族 */
  margin: 0;
  padding: 0;
  box-sizing: border-box; /* 确保盒模型一致 */
`;

const BarContainer = styled.div`
  flex: 1;
  height: 18px;
  background: transparent;
  position: relative;
  margin-left: -8px; /* 进一步左移 */
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 0; /* 取消圆角，改为方形 */
  background: ${({ color }) => color || '#6366f1'};
  width: ${({ percent }) => `${percent}%`};
  transition: width 0.3s ease;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 4px;
  box-sizing: border-box;
  font-size: 12px;
  color: #ffffff;
`;

// 左侧与进度条之间的白色分隔线
const VerticalDivider = styled.div`
  position: absolute;
  left: 68px; /* 60 + 16 - 8 = 68，随上方 margin-left 调整 */
  top: 16px;
  bottom: 16px;
  width: 2px;
  background: #ffffff;
  opacity: 0.9;
  border-radius: 1px;
`;

export default InterviewPage;
